import { SYSTEM_LOINC } from '@ctw/shared/api/fhir/system-urls';
import type { CodeableConcept } from 'fhir/r4';

export const LOINC_TREND_EXCLUSIONS = [
  '56850-1', // lab interpretation
];

export const LOINC_SUMMARY_OF_EPISODE_NOTE = '34133-9';

export const getLoincDisplay = (code: CodeableConcept): string | undefined => {
  const firstLoincCode = code.coding?.find((code) => code.system === SYSTEM_LOINC);

  if (!firstLoincCode) {
    return code.text;
  }

  return (
    (firstLoincCode.code && LoincDisplays[firstLoincCode.code]) ||
    firstLoincCode.display ||
    code.text
  );
};

// Below are the proper display names for LOINC codes commonly used for CDA document types and categories.
// Until we have better LOINC display name enrichment on all DocumentReference resources, we can use these to
// display a cleaner set of names in the filters.
export const LoincDisplays: Record<string, string> = {
  '34534-8': '12 lead EKG panel',
  '93892-8': 'Addiction medicine Progress note',
  '67851-6': 'Admission evaluation note',
  '51849-8': 'Admission history and physical note',
  '89878-3': 'Adolescent medicine Outpatient Note',
  '68617-0': 'Adolescent medicine Progress note',
  '42348-3': 'Advance healthcare directives',
  '53243-2': 'Advanced beneficiary notice',
  '86449-6': 'Against medical advice note',
  '48765-2': 'Allergies and adverse reactions Document',
  '77429-9': 'Allergy and Immunology Consult note',
  '68629-5': 'Allergy and Immunology Note',
  '84057-9': 'Allergy and Immunology Outpatient Note',
  '68631-1': 'Allergy and Immunology Progress note',
  '18682-5': 'Ambulance records',
  '18754-2': 'Ambulatory cardiac rhythm monitor (Holter) study',
  '11485-0': 'Anesthesia records',
  '77403-4': 'Anesthesiology Consult note',
  '94515-4': 'Anesthesiology Discharge summary',
  '34750-0': 'Anesthesiology Note',
  '84060-3': 'Anesthesiology Outpatient Note',
  '84061-1': 'Anesthesiology Postoperative evaluation and management note',
  '34751-8': 'Anesthesiology Preoperative evaluation and management note',
  '84062-9': 'Anesthesiology procedure note',
  '89811-4': 'Anesthesiology Progress note',
  '84064-5': 'Annual evaluation note',
  '57078-8': 'Antenatal testing and surveillance Narrative',
  '57055-6': 'Antepartum summary note',
  '56446-8': 'Appointment summary Document',
  '69981-9': 'Asthma action plan',
  '75424-2': 'Audiology Consult note',
  '68636-0': 'Audiology Note',
  '77426-5': 'Audiology Progress note',
  '630-4': 'Bacteria identified in Urine by Culture',
  '85517-1': 'Bariatric surgery Consult note',
  '85516-3': 'Bariatric surgery Note',
  '85515-5': 'Bariatric surgery Progress note',
  '33720-4': 'Blood bank consult',
  '84067-8': 'Blood banking and transfusion medicine Note',
  '89796-7': 'Blood banking and transfusion medicine procedure note',
  '34532-2': 'Blood type and Indirect antibody screen panel - Blood',
  '8302-2': 'Body height',
  '29463-7': 'Body weight',
  '48807-2': 'Bone marrow aspiration report',
  '94463-7': 'Bone Marrow Transplant Consult note',
  '101523-9':
    'Bovine parainfluenza virus 3 neutralizing antibody [Titer] in Serum by Neutralization test',
  '100968-7': 'Bovine tuberculosis comparative cervical test panel - Skin',
  '18744-3': 'Bronchoscopy study',
  '90012-6': 'Burn management Consult note',
  '90014-2': 'Burn management Progress note',
  '72135-7': 'Cancer diagnosis Narrative',
  '72134-0': 'Cancer event report',
  '19894-5':
    'Carbon dioxide [Partial pressure] in Capillary blood by Transcutaneous CO2 monitor --during chest physiotherapy',
  'LP29708-2': 'Card',
  '18745-0': 'Cardiac catheterization study',
  '18106-5': 'Cardiac echo study Procedure',
  '18750-0': 'Cardiac electrophysiology study',
  '59282-4': 'Cardiac stress echo study',
  '98857-6': 'Cardiac stress EKG study Document',
  '76645-1': 'Cardiac stress test EKG study Type',
  '80575-4': 'Cardiac surgery Consult note',
  '95592-2': 'Cardiac surgery Outpatient Note',
  '80579-6': 'Cardiac surgery Surgical operation note',
  '34099-2': 'Cardiology Consult note',
  '68472-0': 'Cardiology Hospital Progress note',
  '94467-8': 'Cardiology Intensive care unit Progress note',
  '34752-6': 'Cardiology Note',
  '83532-2': 'Cardiology Nurse Outpatient Note',
  '34753-4': 'Cardiology Outpatient Note',
  '75426-7': 'Cardiology procedure note',
  '78710-1': 'Cardiology Progress note',
  '26441-6': 'Cardiology studies (set)',
  '86208-6': 'Cardiology Telehealth Note',
  '78498-3': 'Cardiopulmonary Consult note',
  '34849-0': 'Cardiothoracic surgery Consult note',
  '64061-5': 'Cardiothoracic surgery Hospital Progress note',
  '85436-4': 'Cardiothoracic surgery Note',
  '34850-8': 'Cardiothoracic surgery Outpatient Note',
  '91983-7': 'Care management note',
  '86744-0': 'Care team',
  '85222-8': 'Case manager Consult note',
  '46210-1': 'Case manager Note',
  '85225-1': 'Case manager Outpatient Note',
  '45714-3': 'Chest pain while doing usual activities [Minimum Data Set]',
  '10154-3': 'Chief complaint Narrative - Reported',
  '46239-0': 'Chief complaint+Reason for visit Narrative',
  '84071-0': 'Chiropractic medicine Consult note',
  '85886-0': 'Clinical cardiac electrophysiology Consult note',
  '94518-8': 'Clinical cardiac electrophysiology Progress note',
  '75321-0': 'Clinical finding',
  '78254-0': 'Clinical genetics Consult note',
  '89877-5': 'Clinical genetics Outpatient Note',
  '68659-2': 'Clinical genetics Progress note',
  '55752-0': 'Clinical information',
  '103171-5':
    'Clostridioides difficile toxin A+B DNA [Presence] in Wound by NAA with probe detection',
  '78250-8': 'Colon and rectal surgery Consult note',
  '84073-6': 'Colon and rectal surgery Note',
  '84074-4': 'Colon and rectal surgery procedure note',
  '97575-5': 'Colon and rectal surgery Progress note',
  '18746-8': 'Colonoscopy study',
  '55109-3': 'Complications Document',
  '59284-0': 'Consent Document',
  '11488-4': 'Consult note',
  '28569-2': 'Consultant Progress note',
  '72231-4': 'Consultation note - recommended C-CDA R1.1 sections',
  '47042-7': 'Counseling note',
  '78496-7': 'Critical care medicine Consult note',
  '96146-6': 'Critical care medicine Evaluation note',
  '77420-8': 'Critical care medicine History and physical note',
  '64059-9': 'Critical care medicine Hospital Progress note',
  '77422-4': 'Critical care medicine procedure note',
  '41806-1': 'CT Abdomen',
  '44115-4': 'CT Abdomen and Pelvis',
  '36813-4': 'CT Abdomen and Pelvis W contrast IV',
  '42274-1': 'CT Abdomen and Pelvis WO and W contrast IV',
  '36952-0': 'CT Abdomen and Pelvis WO contrast',
  '79103-8': 'CT Abdomen W contrast IV',
  '87862-9': 'CT Abdomen WO and CT Abdomen and Pelvis W contrast IV',
  '36267-3': 'CT Abdomen WO and W contrast IV',
  '36424-0': 'CT Abdomen WO contrast',
  '36137-8': 'CT Ankle - left W contrast IV',
  '36426-5': 'CT Ankle - left WO contrast',
  '36139-4': 'CT Ankle - right W contrast IV',
  '36428-1': 'CT Ankle - right WO contrast',
  '24932-6': 'CT Cervical spine',
  '79078-2': 'CT Cervical spine by reconstruction W contrast IV',
  '79079-0': 'CT Cervical spine by reconstruction WO contrast',
  '24933-4': 'CT Cervical spine W contrast IV',
  '36401-8': 'CT Cervical spine WO and W contrast IV',
  '30592-0': 'CT Cervical spine WO contrast',
  '24627-2': 'CT Chest',
  '72254-6': 'CT Chest and Abdomen and Pelvis W contrast IV',
  '72252-0': 'CT Chest and Abdomen and Pelvis WO and W contrast IV',
  '72253-8': 'CT Chest and Abdomen and Pelvis WO contrast',
  '42275-8': 'CT Chest and Abdomen W contrast IV',
  '42276-6': 'CT Chest and Abdomen WO contrast',
  '87279-6': 'CT Chest for screening',
  '79086-5': 'CT Chest for screening WO contrast',
  '24628-0': 'CT Chest W contrast IV',
  '30598-7': 'CT Chest WO and W contrast IV',
  '29252-4': 'CT Chest WO contrast',
  '60515-4': 'CT Colon and Rectum W air contrast PR',
  '36159-2': 'CT Elbow - left W contrast IV',
  '36445-5': 'CT Elbow - left WO contrast',
  '36447-1': 'CT Elbow - right WO contrast',
  '24696-7': 'CT Facial bones',
  '24697-5': 'CT Facial bones W contrast IV',
  '72249-6': 'CT Facial bones WO contrast',
  '36181-6': 'CT Foot - left W contrast IV',
  '36468-7': 'CT Foot - left WO contrast',
  '36183-2': 'CT Foot - right W contrast IV',
  '36470-3': 'CT Foot - right WO contrast',
  '36187-3': 'CT Forearm - left W contrast IV',
  '36473-7': 'CT Forearm - left WO contrast',
  '36189-9': 'CT Forearm - right W contrast IV',
  '58747-7': 'CT Guidance for ablation of tissue of Unspecified body region',
  '35887-9': 'CT Guidance for aspiration of cyst of Unspecified body region',
  '37211-0': 'CT Guidance for biopsy of Bone marrow',
  '42279-0': 'CT Guidance for biopsy of Kidney',
  '88325-6': 'CT Guidance for biopsy of Kidney - left',
  '88323-1': 'CT Guidance for biopsy of Kidney - right',
  '24812-0': 'CT Guidance for biopsy of Liver',
  '24823-7': 'CT Guidance for biopsy of Lung',
  '88324-9': 'CT Guidance for biopsy of Lung - left',
  '88526-9': 'CT Guidance for biopsy of Lung - right',
  '35901-8': 'CT Guidance for biopsy of Lymph node',
  '44117-0': 'CT Guidance for biopsy of Retroperitoneum',
  '25044-9': 'CT Guidance for biopsy of Unspecified body region',
  '43567-7': 'CT Guidance for deep biopsy of Bone',
  '35916-6': 'CT Guidance for drainage and placement of drainage catheter of Chest',
  '35919-0': 'CT Guidance for drainage and placement of drainage catheter of Liver',
  '89623-3': 'CT Guidance for drainage and placement of drainage catheter of Peritoneal space',
  '35922-4':
    'CT Guidance for drainage and placement of drainage catheter of Unspecified body region',
  '30578-9':
    'CT Guidance for drainage of abscess and placement of drainage catheter of Unspecified body region',
  '30580-5': 'CT Guidance for fine needle aspiration of Unspecified body region',
  '37491-8': 'CT Guidance for fluid aspiration of Pleural space',
  '25043-1': 'CT Guidance for fluid aspiration of Unspecified body region',
  '24901-1': 'CT Guidance for injection of Sacroiliac Joint',
  '35891-1': 'CT guidance for percutaneous biopsy of Bone',
  '35937-2': 'CT Guidance for placement of radiation therapy fields in Unspecified body region',
  '35938-0': 'CT Guidance for placement of tube in Chest',
  '88321-5': 'CT Guidance for stereotactic localization of Unspecified body region',
  '36193-1': 'CT Hand - left W contrast IV',
  '36195-6': 'CT Hand - right W contrast IV',
  '36480-2': 'CT Hand - right WO contrast',
  '24725-4': 'CT Head',
  '82690-9': 'CT Head and Cervical spine WO contrast',
  '87911-4': 'CT Head and Maxillofacial region WO and W contrast IV',
  '83306-1': 'CT Head and Maxillofacial region WO contrast',
  '82692-5': 'CT Head and Neck WO contrast',
  '86992-5': 'CT Head and Sinuses WO contrast',
  '24727-0': 'CT Head W contrast IV',
  '24726-2': 'CT Head WO and W contrast IV',
  '30799-1': 'CT Head WO contrast',
  '58744-4': 'CT Heart',
  '79087-3': 'CT Heart and Coronary arteries for calcium scoring WO contrast',
  '79089-9': 'CT Heart W contrast IV',
  '36485-1': 'CT Hip - bilateral WO contrast',
  '36203-8': 'CT Hip - left W contrast IV',
  '36487-7': 'CT Hip - left WO contrast',
  '36205-3': 'CT Hip - right W contrast IV',
  '36489-3': 'CT Hip - right WO contrast',
  '30583-9': 'CT Internal auditory canal W contrast IV',
  '36282-2': 'CT Internal auditory canal WO and W contrast IV',
  '30584-7': 'CT Internal auditory canal WO contrast',
  '87866-0': 'CT Kidney and Ureter and Urinary bladder WO and W contrast IV',
  '46322-4': 'CT Kidney W contrast IV',
  '43768-1': 'CT Kidney WO and W contrast IV',
  '36225-1': 'CT Knee - left W contrast IV',
  '36507-2': 'CT Knee - left WO contrast',
  '36227-7': 'CT Knee - right W contrast IV',
  '36509-8': 'CT Knee - right WO contrast',
  '35971-1': 'CT Lower extremity',
  '50755-8': 'CT Lower extremity - bilateral W contrast IV',
  '36449-7': 'CT Lower extremity - bilateral WO contrast',
  '36164-2': 'CT Lower extremity - left W contrast IV',
  '36452-1': 'CT Lower extremity - left WO contrast',
  '36166-7': 'CT Lower extremity - right W contrast IV',
  '36454-7': 'CT Lower extremity - right WO contrast',
  '30624-1': 'CT Lower extremity W contrast IV',
  '30625-8': 'CT Lower extremity WO contrast',
  '36260-8': 'CT Lower leg - left W contrast IV',
  '36538-7': 'CT Lower leg - left WO contrast',
  '36262-4': 'CT Lower leg - right W contrast IV',
  '36540-3': 'CT Lower leg - right WO contrast',
  '24963-1': 'CT Lumbar spine',
  '87867-8': 'CT Lumbar spine by reconstruction',
  '79080-8': 'CT Lumbar spine by reconstruction W contrast IV',
  '79081-6': 'CT Lumbar spine by reconstruction WO contrast',
  '24965-6': 'CT Lumbar spine W contrast IT',
  '24964-9': 'CT Lumbar spine W contrast IV',
  '36402-6': 'CT Lumbar spine WO and W contrast IV',
  '30620-9': 'CT Lumbar spine WO contrast',
  '37439-7': 'CT Lung parenchyma',
  '37440-5': 'CT Lung parenchyma W contrast IV',
  '37441-3': 'CT Lung parenchyma WO contrast',
  '30801-5': 'CT Maxillofacial region W contrast IV',
  '30803-1': 'CT Maxillofacial region WO and W contrast IV',
  '30802-3': 'CT Maxillofacial region WO contrast',
  '36051-1': 'CT Neck',
  '36235-0': 'CT Neck W contrast IV',
  '30586-2': 'CT Neck WO and W contrast IV',
  '36514-8': 'CT Neck WO contrast',
  '89283-6': 'CT Neck+Chest+Abdomen+Pelvis W contrast IV',
  '24850-0': 'CT Orbit - bilateral W contrast IV',
  '30587-0': 'CT Orbit - bilateral WO contrast',
  '48449-3': 'CT Orbit W contrast IV',
  '48451-9': 'CT Orbit WO and W contrast IV',
  '46331-5': 'CT Orbit WO contrast',
  '24865-8': 'CT Pelvis',
  '24866-6': 'CT Pelvis W contrast IV',
  '30616-7': 'CT Pelvis WO and W contrast IV',
  '30615-9': 'CT Pelvis WO contrast',
  '24728-8': 'CT perfusion Head',
  '39142-5': 'CT perfusion Head W contrast IV',
  '43445-6': 'CT Pulmonary system',
  '36252-5': 'CT Shoulder - left W contrast IV',
  '36526-2': 'CT Shoulder - left WO contrast',
  '36253-3': 'CT Shoulder - right W contrast IV',
  '36527-0': 'CT Shoulder - right WO contrast',
  '30588-8': 'CT Sinuses',
  '36255-8': 'CT Sinuses W contrast IV',
  '36529-6': 'CT Sinuses WO contrast',
  '72250-4': 'CT Small bowel W contrast PO and W contrast IV',
  '82697-4': 'CT Spine Lumbar and Sacrum WO contrast',
  '36773-0': 'CT Temporal bone',
  '87903-1': 'CT Temporal bone - bilateral WO contrast',
  '36815-9': 'CT Temporal bone W contrast IV',
  '36866-2': 'CT Temporal bone WO contrast',
  '36174-1': 'CT Thigh - left W contrast IV',
  '36462-0': 'CT Thigh - left WO contrast',
  '36176-6': 'CT Thigh - right W contrast IV',
  '36464-6': 'CT Thigh - right WO contrast',
  '83294-9': 'CT Thoracic and lumbar spine W contrast IV',
  '83310-3': 'CT Thoracic and lumbar spine WO contrast',
  '24978-9': 'CT Thoracic spine',
  '87875-1': 'CT Thoracic spine by reconstruction',
  '79082-4': 'CT Thoracic spine by reconstruction W contrast IV',
  '79091-5': 'CT Thoracic spine by reconstruction WO contrast',
  '24979-7': 'CT Thoracic spine W contrast IV',
  '36403-4': 'CT Thoracic spine WO and W contrast IV',
  '30597-9': 'CT Thoracic spine WO contrast',
  '25045-6': 'CT Unspecified body region',
  '89833-8': 'CT Unspecified body region by reconstruction',
  '25039-9': 'CT Unspecified body region limited',
  '36211-1': 'CT Upper arm - right W contrast IV',
  '36169-1': 'CT Upper extremity - left W contrast IV',
  '36335-8': 'CT Upper extremity - left WO and W contrast IV',
  '36457-0': 'CT Upper extremity - left WO contrast',
  '36170-9': 'CT Upper extremity - right W contrast IV',
  '36458-8': 'CT Upper extremity - right WO contrast',
  '30626-6': 'CT Upper extremity W contrast IV',
  '30627-4': 'CT Upper extremity WO contrast',
  '37463-7': 'CT Wrist - left WO contrast',
  '37465-2': 'CT Wrist - right WO contrast',
  '78037-9': 'CTA Abdominal Aorta and Bilateral Runoff Vessels W contrast IV',
  '78031-2': 'CTA Abdominal Aorta and Bilateral Runoff Vessels WO and W contrast IV',
  '87838-9': 'CTA Abdominal veins and Pelvis veins W contrast IV',
  '69908-2': 'CTA Abdominal vessels and Pelvis vessels W contrast IV',
  '82679-2': 'CTA Abdominal vessels and Pelvis vessels WO and W contrast IV',
  '36828-2': 'CTA Abdominal vessels W contrast IV',
  '30805-6': 'CTA Abdominal vessels WO and W contrast IV',
  '86982-6': 'CTA Chest and abdominal vessels W contrast IV',
  '86971-9': 'CTA Chest and abdominal vessels WO and W contrast IV',
  '86981-8': 'CTA Chest vessels',
  '86983-4': 'CTA Chest vessels and Abdominal vessels and Pelvis vessels W contrast IV',
  '83298-0': 'CTA Chest vessels and Abdominal vessels and Pelvis vessels WO and W contrast IV',
  '36266-5': 'CTA Chest vessels W contrast IV',
  '30804-9': 'CTA Chest vessels WO and W contrast IV',
  '37498-3': 'CTA Head vessels and Neck vessels W contrast IV',
  '82680-0': 'CTA Head vessels and Neck vessels WO and W contrast IV',
  '36830-8': 'CTA Head vessels W contrast IV',
  '30593-8': 'CTA Head vessels WO and W contrast IV',
  '79073-3': 'CTA Heart and Coronary arteries W contrast IV',
  '82711-3': 'CTA Lower extremity vessels - bilateral',
  '82712-1': 'CTA Lower extremity vessels - left',
  '86964-4': 'CTA Lower extremity vessels - left W contrast IV',
  '46308-3': 'CTA Lower extremity vessels - left WO and W contrast IV',
  '82710-5': 'CTA Lower extremity vessels - right',
  '86963-6': 'CTA Lower extremity vessels - right W contrast IV',
  '46307-5': 'CTA Lower extremity vessels - right WO and W contrast IV',
  '36234-3': 'CTA Neck vessels W contrast IV',
  '30594-6': 'CTA Neck vessels WO and W contrast IV',
  '42294-9': 'CTA Pelvis vessels W contrast IV',
  '30623-3': 'CTA Pelvis vessels WO and W contrast IV',
  '79077-4': 'CTA Pulmonary arteries for pulmonary embolus W contrast IV',
  '78032-0': 'CTA Thoracic and Abdominal Aorta and Bilateral Runoff Vessels WO and W contrast IV',
  '82713-9': 'CTA Upper extremity vessels - left',
  '86961-0': 'CTA Upper extremity vessels - left W contrast IV',
  '46312-5': 'CTA Upper extremity vessels - left WO and W contrast IV',
  '82714-7': 'CTA Upper extremity vessels - right',
  '46309-1': 'CTA Upper extremity vessels - right WO and W contrast IV',
  '55228-1': 'Cytogenetics study',
  '19767-3': 'Cytologist who read Cyto stain of Cervical or vaginal smear or scraping',
  '47528-5': 'Cytology report of Cervical or vaginal smear or scraping Cyto stain',
  '47527-7': 'Cytology report of Cervical or vaginal smear or scraping Cyto stain.thin prep',
  '47526-9': 'Cytology report of Specimen Cyto stain',
  '86463-7': 'DBT Breast - bilateral',
  '72139-9': 'DBT Breast - bilateral diagnostic',
  '91522-3': 'DBT Breast - bilateral screen for implant',
  '72142-3': 'DBT Breast - bilateral screening',
  '72138-1': 'DBT Breast - left diagnostic',
  '72141-5': 'DBT Breast - left screening',
  '72137-3': 'DBT Breast - right diagnostic',
  '72140-7': 'DBT Breast - right screening',
  '34756-7': 'Dentistry Consult note',
  '28617-9': 'Dentistry Progress note',
  '27447-2':
    'Deprecated Cardiac rehabilitation treatment plan Progress note and attainment of goals (narrative)',
  '42289-9': 'Deprecated CT Guidance for needle biopsy of Kidney',
  '52521-2': 'Deprecated Overall Plan of Care/Advance Care Directives',
  '62616-8': 'Deprecated PhenX measure - respiratory - chest computed tomography - CT',
  '44170-9': 'Deprecated US Guidance for needle biopsy of Liver',
  '34758-3': 'Dermatology Consult note',
  '34759-1': 'Dermatology Note',
  '84080-1': 'Dermatology Outpatient Note',
  '78314-2': 'Dermatology procedure note',
  '78326-6': 'Dermatology Progress note',
  '68666-7': 'Developmental-behavioral pediatrics Note',
  '89881-7': 'Developmental-behavioral pediatrics Outpatient Note',
  '34897-9': 'Diabetology Education note',
  '34861-5': 'Diabetology Note',
  '34110-7': 'Diabetology Outpatient Note',
  '29308-4': 'Diagnosis',
  '18748-4': 'Diagnostic imaging study',
  '47048-4': 'Diagnostic interventional radiology study',
  '95134-3': 'Dialysis and Therapeutic apheresis Progress note',
  '87233-3': 'Dialysis Consult note',
  '87238-2': 'Dialysis Note',
  '29749-9': 'Dialysis records',
  '87236-6': 'Dialysis Summary note',
  '104055-9':
    'Did you work remotely at home completely or only on several days per week during last 2 weeks',
  '61144-2': 'Diet and nutrition Narrative',
  '74213-0': 'Discharge instructions',
  '18842-5': 'Discharge summary',
  'LP72467-1': 'Discharge summary note',
  '71440-2': 'Discharge type [CMS Assessment]',
  '46383-6': 'DXA Bone [Mass/Area] Bone density',
  '38268-9': 'DXA Skeletal system Views for bone density',
  '83012-5': 'DXA Skeletal system.axial Views for bone density',
  '83014-1': 'DXA Skeletal system.axial Views for bone density and vertebral fracture',
  '83013-3': 'DXA Skeletal system.peripheral Views for bone density',
  '34895-3': 'Education note',
  '11523-8': 'EEG study',
  '28014-9': 'EGD Study',
  '11524-6': 'EKG study',
  '18810-2': 'EKG Study observation overall (narrative)',
  '18749-2': 'Electromyogram study',
  '98141-5': 'Emergency department Admission notification',
  '60280-5': 'Emergency department Discharge instructions',
  '59258-4': 'Emergency department Discharge summary',
  '78273-0': 'Emergency department Education note',
  '28011-5': 'Emergency department Medical records',
  '34111-5': 'Emergency department Note',
  '78316-7': 'Emergency department procedure note',
  '15507-7': 'Emergency department Progress note',
  '96335-5': 'Emergency department Summary note',
  '54094-8': 'Emergency department Triage note',
  '80110-0': 'Emergency department Visit notification',
  '97670-4': 'Emergency medicine Emergency department Transfer summary note',
  '34879-7': 'Endocrinology Consult note',
  '34898-7': 'Endocrinology Note',
  '83573-6': 'Endocrinology Nurse Outpatient Note',
  '84099-1': 'Endocrinology Outpatient Note',
  '78327-4': 'Endocrinology Progress note',
  '89891-6': 'Endocrinology Telehealth Note',
  '18751-8': 'Endoscopy study',
  '28016-4': 'ERCP study',
  '51393-7': 'Esophageal manometry study',
  '86451-2': 'Ethics Consult note',
  '51847-2': 'Evaluation + Plan note',
  '34856-5': 'Evaluation and management of anticoagulation note',
  '51848-0': 'Evaluation note',
  '77199-8': 'Evoked potential study',
  '18752-6': 'Exercise stress test study',
  '78253-2': 'Family medicine Consult note',
  '85437-2': 'Family medicine Note',
  '78315-9': 'Family medicine procedure note',
  '78324-1': 'Family medicine Progress note',
  '48770-2': 'Fetal Biophysical profile panel US',
  '11616-0': 'Fetal Heart Narrative Activity US',
  '53660-7': 'Fetal Heart Narrative Study observation US',
  '42452-3': 'Fetal Narrative [Interpretation] Study observation.general 1st trimester US',
  '42453-1': 'Fetal Narrative [Interpretation] Study observation.general 2nd trimester US',
  '30707-4': 'Fetal Narrative [Interpretation] Study observation.general.follow-up US',
  '33069-6': 'Fetal nuchal translucency measured by US',
  '11660-8': 'Fetal Umbilical artery at fetus Minimal flow velocity US.doppler',
  '39154-0': 'FFD mammogram Breast - bilateral Diagnostic',
  '42169-3': 'FFD mammogram Breast - left Diagnostic',
  '42168-5': 'FFD mammogram Breast - right Diagnostic',
  '39153-2': 'FFD mammogram Breast Screening',
  '18753-4': 'Flexible sigmoidoscopy study',
  '33719-6': 'Flow cytometry study',
  '80568-9': 'Form',
  '57129-9': 'Full newborn screening summary report for display or printing',
  '47420-5': 'Functional status assessment note',
  '34761-7': 'Gastroenterology Consult note',
  '27895-2': 'Gastroenterology endoscopy studies (set)',
  '84104-9': 'Gastroenterology History and physical note',
  '34762-5': 'Gastroenterology Note',
  '84107-2': 'Gastroenterology Outpatient Note',
  '78317-5': 'Gastroenterology procedure note',
  '78323-3': 'Gastroenterology Progress note',
  '94780-4': 'Gastroenterology Telehealth Note',
  '52033-8': 'General correspondence attachment',
  '34764-1': 'General medicine Consult note',
  '64053-2': 'General medicine Hospital Admission evaluation note',
  '78335-7': 'General medicine Initial evaluation note',
  '34765-8': 'General medicine Note',
  '34766-6': 'General medicine Outpatient Note',
  '34900-1': 'General medicine Progress note',
  '84119-7': 'Geriatric medicine Outpatient Note',
  '80815-4': 'Geriatric medicine Progress note',
  '77147-7': 'Glomerular filtration rate [Volume Rate/Area] in Serum',
  '41653-7': 'Glucose [Mass/volume] in Capillary blood by Glucometer',
  '93791-2': 'Glucose [Mass/volume] mean in Serum or Plasma',
  '61146-7': 'Goals Narrative',
  '87054-3': 'Guidance for exchange of nephrostomy tube of Kidney',
  '87080-8': 'Guidance for paracentesis of Peritoneum',
  '80396-5': 'Gynecologic oncology Consult note',
  '85444-8': 'Gynecologic oncology Note',
  '100468-8': 'Gynecologic oncology Outpatient Progress note',
  '89802-3': 'Gynecologic oncology Progress note',
  '89216-6': 'Gynecology Consult note',
  '85937-1': 'Gynecology Note',
  '89461-8': 'Gynecology Outpatient Note',
  '89223-2': 'Gynecology procedure note',
  '89224-0': 'Gynecology Progress note',
  '11947-9': 'Head circumference/Abdominal circumference derived by US',
  '75310-3': 'Health concerns Document',
  '100000-9': 'Health informatics pioneer and the father of LOINC',
  '97711-6': 'Heart failure Outpatient Note',
  '90772-5': 'Heart failure Progress note',
  '68621-2': 'Heart failure+Transplant cardiology Note',
  '78251-6': 'Hematology Consult note',
  '85443-0': 'Hematology Note',
  '96337-1': 'Hematology Outpatient Note',
  '78328-2': 'Hematology Progress note',
  '34779-9': 'Hematology+Medical oncology Consult note',
  '84146-0': 'Hematology+Medical oncology Outpatient Note',
  '68554-5': 'Hematology+Medical oncology Progress note',
  '84157-7': 'Hepatology Outpatient Note',
  '34117-2': 'History and physical note',
  'LP72701-3': 'History and physical note',
  '11329-0': 'History general Narrative - Reported',
  '10157-6': 'History of family member diseases note',
  '46240-8': 'History of Hospitalizations+Outpatient visits Narrative',
  '11369-6': 'History of Immunization note',
  '46264-8': 'History of medical device use',
  '10160-0': 'History of Medication use Narrative',
  '11348-0': 'History of Past illness note',
  '10162-6': 'History of pregnancies Narrative',
  '10164-2': 'History of Present illness Narrative',
  '47519-4': 'History of Procedures Document',
  '10167-5': 'History of Surgical procedures Narrative',
  '10182-4': 'History of Travel note',
  '100018-1': 'Hospice care Note',
  '46241-6': 'Hospital admission diagnosis Narrative - Reported',
  '47039-3': 'Hospital Admission history and physical note',
  '34104-0': 'Hospital Consult note',
  '18841-7': 'Hospital consultations Document',
  '8648-8': 'Hospital course note',
  '11535-2': 'Hospital discharge diagnosis note',
  '8653-8': 'Hospital Discharge instructions',
  '10183-2': 'Hospital discharge medications Narrative',
  '10184-0': 'Hospital discharge physical findings Narrative',
  '11493-4': 'Hospital discharge studies summary Narrative',
  '34105-7': 'Hospital Discharge summary',
  '34112-3': 'Hospital Note',
  '68610-5': 'Hospital Postoperative evaluation and management note',
  '34130-5': 'Hospital Progress note',
  '96339-7': 'Hospital Summary note',
  '104067-4': 'Hospitalist Assessment note',
  '101005-7': 'Hospitalist Discharge summary',
  '101046-1': 'Hospitalist Progress note',
  '100305-2':
    'IDH1 gene exon 4 targeted mutation analysis [Presence] in Blood or Marrow by Molecular genetics method',
  '87273-9': 'Immunization note',
  '82593-5': 'Immunization summary report',
  '92914-1': 'Immunology Outpatient Note',
  '34781-5': 'Infectious disease Consult note',
  '34782-3': 'Infectious disease Note',
  '83627-0': 'Infectious disease Nurse Outpatient Note',
  '84169-2': 'Infectious disease Outpatient Note',
  '78325-8': 'Infectious disease Progress note',
  '100345-8':
    'Influenza virus A and B and SARS-CoV-2 (COVID-19) RNA panel - Specimen by NAA with probe detection',
  '69730-0': 'Instructions',
  '96340-5': 'Integrative medicine Note',
  '34126-3': 'Intensive care unit Progress note',
  '34794-8': 'Interdisciplinary Note',
  '85238-4': 'Internal medicine Consult note',
  '75435-8': 'Internal medicine Note',
  '75436-6': 'Internal medicine Outpatient Note',
  '97578-9': 'Internal medicine Progress note',
  '84173-4': 'Interventional cardiology Consult note',
  '72555-6': 'Interventional radiology Consult note',
  '84178-3': 'Interventional radiology Note',
  '75238-6': 'Interventional radiology procedure note',
  '72556-4': 'Interventional radiology Progress note',
  '94787-9': 'Interventional radiology Telehealth Note',
  '62387-6': 'Interventions Narrative',
  'LP29693-6': 'Lab',
  '15508-5': 'Labor and delivery records',
  '57057-2': 'Labor and delivery summary note',
  'LP75011-4': 'Laboratory',
  '11502-2': 'Laboratory report',
  '26436-6': 'Laboratory studies (set)',
  '51852-2': 'Letter',
  '77615-3': 'Liver stiffness by US.transient elastography',
  '100946-3': 'Loneliness (ONS) score',
  '51854-8': 'Long term care facility Consult note',
  '75438-2': 'Long term care facility Note',
  '100735-0': 'Mannose-6-phosphate isomerase and phosphomannomutase 1 panel - Leukocytes',
  '80673-7': 'Maternal and fetal medicine Consult note',
  '85439-8': 'Maternal and fetal medicine Note',
  '93897-7': 'Maternal and fetal medicine Progress note',
  '11503-0': 'Medical records',
  '75439-0': 'Medical student Note',
  '29549-3': 'Medication administered Narrative',
  '18610-6': 'Medication administered Set',
  '80565-5': 'Medication administration record',
  '70006-2': 'Medication management note',
  '42346-7': 'Medications on admission (narrative)',
  '34785-6': 'Mental health Consult note',
  '78263-1': 'Mental health Discharge summary',
  '34786-4': 'Mental health Note',
  '75443-2': 'Mental health Outpatient Note',
  '34904-3': 'Mental health Progress note',
  '10190-7': 'Mental status Narrative',
  '26346-7': 'MG Breast - bilateral Diagnostic',
  '26175-0': 'MG Breast - bilateral Screening',
  '48492-3': 'MG Breast - bilateral Screening for implant',
  '36626-0': 'MG Breast - bilateral Views',
  '26347-5': 'MG Breast - left Diagnostic',
  '26289-9': 'MG Breast - left Limited Views',
  '26176-8': 'MG Breast - left Screening',
  '36627-8': 'MG Breast - left Views',
  '26348-3': 'MG Breast - right Diagnostic',
  '86360-5': 'MG Breast - right Guidance for needle localization and Breast specimen Views',
  '26291-5': 'MG Breast - right Limited Views',
  '26177-6': 'MG Breast - right Screening',
  '37774-7': 'MG Breast - right Views',
  '46350-5': 'MG Breast - unilateral Diagnostic',
  '46356-2': 'MG Breast - unilateral Screening',
  '24605-8': 'MG Breast Diagnostic',
  '24606-6': 'MG Breast Screening',
  '38080-8': 'MG Breast specimen - left Views',
  '38821-5': 'MG Breast specimen - right Views',
  '24597-7': 'MG Breast specimen Views',
  '36625-2': 'MG Breast Views',
  '86357-1': 'MG Guidance for needle localization of Breast - left',
  '86358-9': 'MG Guidance for needle localization of Breast - right',
  '26316-0': 'MG Guidance for needle localization of mass of Breast - left',
  '26335-0': 'MG Guidance for percutaneous biopsy.core needle of Breast - left',
  '24603-3': 'MG stereo Guidance for biopsy of Breast',
  '26293-1': 'MG stereo Guidance for biopsy of Breast - left',
  '26294-9': 'MG stereo Guidance for biopsy of Breast - right',
  'LP7819-8': 'MICRO',
  '10524-7': 'Microscopic observation [Identifier] in Cervix by Cyto stain',
  '24556-3': 'MR Abdomen',
  '36134-5': 'MR Abdomen W contrast IV',
  '24557-1': 'MR Abdomen WO and W contrast IV',
  '30668-8': 'MR Abdomen WO contrast',
  '26188-3': 'MR Ankle - left WO and W contrast IV',
  '36427-3': 'MR Ankle - left WO contrast',
  '26210-5': 'MR Ankle - right',
  '26189-1': 'MR Ankle - right WO and W contrast IV',
  '36429-9': 'MR Ankle - right WO contrast',
  '37220-1': 'MR Biliary ducts and Pancreatic duct',
  '36944-7': 'MR Biliary ducts and Pancreatic duct WO and W contrast IV',
  '44123-8': 'MR Biliary ducts and Pancreatic duct WO contrast',
  '26192-5': 'MR Brachial plexus - right WO and W contrast IV',
  '24583-7': 'MR Brachial plexus WO and W contrast IV',
  '24590-2': 'MR Brain',
  '43769-9': 'MR Brain and Internal auditory canal WO and W contrast IV',
  '42392-1': 'MR Brain and Pituitary and Sella turcica WO and W contrast IV',
  '105164-8': 'MR Brain during surgery W and WO contrast IV',
  '36105-5': 'MR Brain limited WO contrast',
  '24589-4': 'MR Brain W contrast IV',
  '24587-8': 'MR Brain WO and W contrast IV',
  '30657-1': 'MR Brain WO contrast',
  '36277-2': 'MR Breast - bilateral WO and W contrast IV',
  '36437-2': 'MR Breast - bilateral WO contrast',
  '30855-1': 'MR Cervical and thoracic and lumbar spine WO and W contrast IV',
  '30854-4': 'MR Cervical and thoracic and lumbar spine WO contrast',
  '24935-9': 'MR Cervical spine',
  '24938-3': 'MR Cervical spine W contrast IV',
  '24937-5': 'MR Cervical spine WO and W contrast IV',
  '30667-0': 'MR Cervical spine WO contrast',
  '36283-0': 'MR Chest WO and W contrast IV',
  '36442-2': 'MR Chest WO contrast',
  '26194-1': 'MR Elbow - left WO and W contrast IV',
  '36446-3': 'MR Elbow - left WO contrast',
  '36448-9': 'MR Elbow - right WO contrast',
  '24694-2': 'MR Face WO and W contrast IV',
  '26242-8': 'MR Foot - left',
  '36182-4': 'MR Foot - left W contrast IV',
  '36344-0': 'MR Foot - left WO and W contrast IV',
  '36469-5': 'MR Foot - left WO contrast',
  '26243-6': 'MR Foot - right',
  '36346-5': 'MR Foot - right WO and W contrast IV',
  '36471-1': 'MR Foot - right WO contrast',
  '36351-5': 'MR Forearm - right WO and W contrast IV',
  '69213-7': 'MR Guidance for biopsy of Breast - right',
  '24720-5': 'MR Hand',
  '36354-9': 'MR Hand - left WO and W contrast IV',
  '36479-4': 'MR Hand - left WO contrast',
  '36356-4': 'MR Hand - right WO and W contrast IV',
  '36481-0': 'MR Hand - right WO contrast',
  '24748-6': 'MR Heart',
  '36197-2': 'MR Heart W contrast IV',
  '58749-3': 'MR Heart W stress and WO and W contrast IV',
  '36357-2': 'MR Heart WO and W contrast IV',
  '36482-8': 'MR Heart WO contrast',
  '36486-9': 'MR Hip - bilateral WO contrast',
  '36020-6': 'MR Hip - left',
  '36204-6': 'MR Hip - left W contrast IV',
  '36363-0': 'MR Hip - left WO and W contrast IV',
  '36488-5': 'MR Hip - left WO contrast',
  '36022-2': 'MR Hip - right',
  '36206-1': 'MR Hip - right W contrast IV',
  '36365-5': 'MR Hip - right WO and W contrast IV',
  '36490-1': 'MR Hip - right WO contrast',
  '30659-7': 'MR Internal auditory canal WO and W contrast IV',
  '24802-1': 'MR Knee',
  '26257-6': 'MR Knee - left',
  '26200-6': 'MR Knee - left WO and W contrast IV',
  '36508-0': 'MR Knee - left WO contrast',
  '26258-4': 'MR Knee - right',
  '26201-4': 'MR Knee - right WO and W contrast IV',
  '36510-6': 'MR Knee - right WO contrast',
  '30670-4': 'MR Liver WO and W contrast IV',
  '36165-9': 'MR Lower extremity - left W contrast IV',
  '36291-3': 'MR Lower extremity - left WO and W contrast IV',
  '36453-9': 'MR Lower extremity - left WO contrast',
  '36167-5': 'MR Lower extremity - right W contrast IV',
  '36333-3': 'MR Lower extremity - right WO and W contrast IV',
  '36455-4': 'MR Lower extremity - right WO contrast',
  '36372-1': 'MR Lower extremity joint - left WO and W contrast IV',
  '36498-4': 'MR Lower extremity joint - left WO contrast',
  '36373-9': 'MR Lower extremity joint - right WO and W contrast IV',
  '36499-2': 'MR Lower extremity joint - right WO contrast',
  '36371-3': 'MR Lower Extremity Joint WO and W contrast IV',
  '39291-0': 'MR Lower extremity WO and W contrast IV',
  '39292-8': 'MR Lower extremity WO contrast',
  '36410-9': 'MR Lower leg - left WO and W contrast IV',
  '36539-5': 'MR Lower leg - left WO contrast',
  '36412-5': 'MR Lower leg - right WO and W contrast IV',
  '36541-1': 'MR Lower leg - right WO contrast',
  '24968-0': 'MR Lumbar spine',
  '30678-7': 'MR Lumbar spine W contrast IV',
  '24967-2': 'MR Lumbar spine WO and W contrast IV',
  '30679-5': 'MR Lumbar spine WO contrast',
  '24840-1': 'MR Neck WO and W contrast IV',
  '30660-5': 'MR Neck WO contrast',
  '24851-8': 'MR Orbit - bilateral WO and W contrast IV',
  '46310-9': 'MR Orbit and Face and Neck WO and W contrast IV',
  '46332-3': 'MR Orbit and Face and Neck WO contrast',
  '36842-3': 'MR Orbit WO and W contrast IV',
  '24867-4': 'MR Pelvis',
  '36237-6': 'MR Pelvis W contrast IV',
  '30674-6': 'MR Pelvis WO and W contrast IV',
  '30673-8': 'MR Pelvis WO contrast',
  '24879-9': 'MR Pituitary and Sella turcica WO and W contrast IV',
  '30675-3': 'MR Prostate',
  '36389-5': 'MR Prostate WO and W contrast IV',
  '36522-1': 'MR Sacrum and Coccyx WO contrast',
  '24905-2': 'MR Shoulder',
  '38830-6': 'MR Shoulder - left W contrast IV',
  '26203-0': 'MR Shoulder - left WO and W contrast IV',
  '38834-8': 'MR Shoulder - left WO contrast',
  '26270-9': 'MR Shoulder - right',
  '36254-1': 'MR Shoulder - right W contrast IV',
  '26204-8': 'MR Shoulder - right WO and W contrast IV',
  '36528-8': 'MR Shoulder - right WO contrast',
  '30693-6': 'MR Shoulder WO contrast',
  '37443-9': 'MR spectroscopy Unspecified body region',
  '36400-0': 'MR Spine WO and W contrast IV',
  '36531-2': 'MR Spine WO contrast',
  '26197-4': 'MR Thigh - left WO and W contrast IV',
  '26198-2': 'MR Thigh - right WO and W contrast IV',
  '24982-1': 'MR Thoracic spine W contrast IV',
  '24981-3': 'MR Thoracic spine WO and W contrast IV',
  '36532-0': 'MR Thoracic spine WO contrast',
  '25056-3': 'MR Unspecified body region',
  '38831-4': 'MR Upper extremity - left WO and W contrast IV',
  '38832-2': 'MR Upper extremity - left WO contrast',
  '36337-4': 'MR Upper extremity - right WO and W contrast IV',
  '36840-7': 'MR Upper extremity joint - left WO and W contrast IV',
  '36869-6': 'MR Upper extremity joint - left WO contrast',
  '36819-1': 'MR Upper extremity joint - right W contrast IV',
  '36870-4': 'MR Upper extremity joint - right WO contrast',
  '36216-0': 'MR Upper extremity.joint W contrast IV',
  '26206-3': 'MR Wrist - left WO and W contrast IV',
  '37464-5': 'MR Wrist - left WO contrast',
  '26207-1': 'MR Wrist - right WO and W contrast IV',
  '37466-0': 'MR Wrist - right WO contrast',
  '36846-4': 'MRA Abdominal veins WO and W contrast IV',
  '24533-2': 'MRA Abdominal vessels W contrast IV',
  '36855-5': 'MRA Abdominal vessels WO and W contrast IV',
  '36420-8': 'MRA Chest vessels WO and W contrast IV',
  '36826-6': 'MRA Head veins W contrast IV',
  '36847-2': 'MRA Head veins WO and W contrast IV',
  '36876-1': 'MRA Head veins WO contrast',
  '30856-9': 'MRA Head vessels',
  '42304-6': 'MRA Head vessels and Neck vessels',
  '24593-6': 'MRA Head vessels W contrast IV',
  '36857-1': 'MRA Head vessels WO and W contrast IV',
  '36881-1': 'MRA Head vessels WO contrast',
  '36882-9': 'MRA Lower extremity vessels - left WO contrast',
  '36859-7': 'MRA Lower extremity vessels - right WO and W contrast IV',
  '24844-3': 'MRA Neck vessels W contrast IV',
  '36423-2': 'MRA Neck vessels WO and W contrast IV',
  '36549-4': 'MRA Neck vessels WO contrast',
  '36883-7': 'MRA Pelvis vessels WO contrast',
  '37497-5': 'MRA Spine vessels',
  '25058-9': 'MRA Unspecified body region W contrast IV',
  '72248-8': 'MRCP Abdomen WO and W contrast IV',
  '58740-2': 'MRCP Abdomen WO contrast',
  '78405-8': 'Neonatal perinatal medicine Consult note',
  '85438-0': 'Neonatal perinatal medicine Note',
  '89880-9': 'Neonatal perinatal medicine Outpatient Note',
  '78473-6': 'Neonatal perinatal medicine procedure note',
  '78479-3': 'Neonatal perinatal medicine Progress note',
  '34795-5': 'Nephrology Consult note',
  '34796-3': 'Nephrology Note',
  '84222-9': 'Nephrology Outpatient Note',
  '78474-4': 'Nephrology procedure note',
  '78480-1': 'Nephrology Progress note',
  '89898-1': 'Nephrology Telehealth Note',
  '29755-6': 'Nerve conduction study',
  '34798-9': 'Neurological surgery Consult note',
  '84237-7': 'Neurological surgery Outpatient Note',
  '68693-1': 'Neurological surgery Progress note',
  '34797-1': 'Neurology Consult note',
  '34905-0': 'Neurology Note',
  '84229-4': 'Neurology Outpatient Note',
  '78475-1': 'Neurology procedure note',
  '75221-2': 'Neurology Progress note',
  '85890-2': 'Neuropsychology Consult note',
  '85876-1': 'Neuropsychology Note',
  '24571-2':
    'NM Biliary ducts and Gallbladder Views for patency of biliary structures and ejection fraction W sincalide and W radionuclide IV',
  '39883-4': 'NM Bone 3 Phase Views',
  '39627-5': 'NM Bone Limited Views',
  '25031-6': 'NM Bone Views',
  '81544-9': 'NM Gastrointestinal tract Views for gastrointestinal bleeding',
  '24714-8': 'NM Gastrointestinal tract Views for gastrointestinal bleeding W Tc-99m tagged RBC IV',
  '39623-4': 'NM Guidance for abscess localization of Whole body',
  '39861-0': 'NM Heart Blood pool',
  '39915-4': 'NM Heart Gated',
  '39716-6': 'NM Heart Perfusion',
  '39726-5': 'NM Heart Perfusion at rest and W stress and W radionuclide IV',
  '39653-1': 'NM Heart Views for infarct',
  '42261-8': 'NM Kidney Views for blood flow',
  '43557-8': 'NM Liver and Biliary ducts and Gallbladder Views',
  '39877-6': 'NM Liver and Spleen Views',
  '39693-7': 'NM Liver Views',
  '39697-8': 'NM Lung Perfusion',
  '39837-0': 'NM Lung Ventilation',
  '39942-8': 'NM Lung Ventilation and Perfusion',
  '39946-9': 'NM Lung Ventilation and Perfusion and Differential',
  '24826-0': 'NM Lymphatic vessels Views W radionuclide intra lymphatic',
  '39742-2': 'NM Parathyroid gland Views',
  '30697-7': 'NM Pulmonary system Ventilation and Perfusion',
  '39629-1': "NM Small bowel Views for Meckel's diverticulum",
  '39751-3': 'NM Spleen Views',
  '39769-5': 'NM Stomach Views for gastric emptying W radionuclide PO',
  '39768-7': 'NM Stomach Views for gastric emptying W Tc-99m SC PO',
  '43672-5': 'NM Thyroid gland Views and Views uptake',
  '39818-0': 'NM Whole body Bone Views',
  '42171-9': 'NM Whole body Views for tumor',
  '100010-8': 'No injury related to procedure equipment',
  '33716-2': 'Non-gynecological cytology study',
  '34109-9': 'Note',
  'LP72988-6': 'Note',
  '85895-1': 'Nuclear medicine Diagnostic study note',
  '34745-0': 'Nurse Discharge summary',
  '57053-1': 'Nurse Emergency department Note',
  '75448-1': 'Nurse Flowsheet',
  '34746-8': 'Nurse Note',
  '75453-1': 'Nurse Outpatient Note',
  '28575-9': 'Nurse practitioner Progress note',
  '28623-7': 'Nurse Progress note',
  '84275-7': 'Nurse Urgent care center Note',
  '11543-6': 'Nursery records',
  '34096-8': 'Nursing facility Comprehensive history and physical note',
  '34800-3': 'Nutrition and dietetics Consult note',
  '84284-9': 'Nutrition and dietetics Hospital Note',
  '34801-1': 'Nutrition and dietetics Note',
  '84289-8': 'Nutrition and dietetics Outpatient Note',
  '78481-9': 'Nutrition and dietetics Progress note',
  '35096-7': 'OB ultrasound panel',
  '61149-1': 'Objective Narrative',
  '34777-3': 'Obstetrics and Gynecology Consult note',
  '34778-1': 'Obstetrics and Gynecology Note',
  '84384-7': 'Obstetrics and Gynecology Outpatient Note',
  '68563-6': 'Obstetrics and Gynecology procedure note',
  '68564-4': 'Obstetrics and Gynecology Progress note',
  '89227-3': 'Obstetrics Consult note',
  '89460-0': 'Obstetrics Outpatient Note',
  '89232-3': 'Obstetrics procedure note',
  '89233-1': 'Obstetrics Progress note',
  '89464-2': 'Obstetrics Telephone encounter Note',
  '34802-9': 'Occupational medicine Note',
  '84390-4': 'Occupational medicine Outpatient Note',
  '34855-7': 'Occupational therapy Consult note',
  '96134-2': 'Occupational therapy Evaluation note',
  '84397-9': 'Occupational therapy Hospital Note',
  '28578-3': 'Occupational therapy Note',
  '84400-1': 'Occupational therapy Outpatient Note',
  '11507-1': 'Occupational therapy Progress note',
  '70948-5': 'Ocular physical exam Narrative',
  '34805-2': 'Oncology Consult note',
  '78421-5': 'Oncology Discharge summary',
  '34806-0': 'Oncology Note',
  '96343-9': 'Oncology Outpatient Note',
  '74156-1': 'Oncology Plan of care and summary note',
  '78478-5': 'Oncology procedure note',
  '78482-7': 'Oncology Progress note',
  '96344-7': 'Oncology Summary note',
  '34807-8': 'Ophthalmology Consult note',
  '34808-6': 'Ophthalmology Note',
  '75462-2': 'Ophthalmology Outpatient Note',
  '84408-4': 'Ophthalmology Outpatient procedure note',
  '78657-4': 'Ophthalmology procedure note',
  '68574-3': 'Ophthalmology Progress note',
  '34810-2': 'Optometry Consult note',
  '84411-8': 'Optometry Outpatient Note',
  '34812-8': 'Oral and Maxillofacial Surgery Consult note',
  '34813-6': 'Oral and Maxillofacial Surgery Note',
  '84381-3': 'Oral and Maxillofacial Surgery Outpatient Note',
  '77597-3': 'Orders placed Document',
  '34814-4': 'Orthopaedic surgery Consult note',
  '34815-1': 'Orthopaedic surgery Note',
  '84376-3': 'Orthopaedic surgery Outpatient Note',
  '84374-8': 'Orthopaedic surgery procedure note',
  '68582-6': 'Orthopaedic surgery Progress note',
  '34816-9': 'Otolaryngology Consult note',
  '34817-7': 'Otolaryngology Note',
  '84364-9': 'Otolaryngology Outpatient Note',
  '78669-9': 'Otolaryngology Progress note',
  '51845-6': 'Outpatient Consult note',
  '67855-7': 'Outpatient Education note',
  '34108-1': 'Outpatient Note',
  '34131-3': 'Outpatient Progress note',
  '96345-4': 'Outpatient Summary note',
  '78567-5': 'Pain medicine Consult note',
  '34858-1': 'Pain medicine Note',
  '83650-2': 'Pain medicine Outpatient Note',
  '83651-0': 'Pain medicine Outpatient procedure note',
  '68714-5': 'Pain medicine procedure note',
  '78668-1': 'Pain medicine Progress note',
  '78568-3': 'Palliative care Consult note',
  '85435-6': 'Palliative care Note',
  '84355-7': 'Palliative care Outpatient Note',
  '78663-2': 'Palliative care Progress note',
  '90715-4': 'Palliative care Telehealth Consult note',
  '80671-1': 'Pastoral care Progress note',
  'LP7839-6': 'PATH',
  '59465-5': 'Pathologist review of results',
  '60570-9': 'Pathology Consult note',
  '22638-1': 'Pathology report comments [Interpretation] Narrative',
  '34574-4': 'Pathology report final diagnosis',
  '22637-3': 'Pathology report final diagnosis Narrative',
  '22634-0': 'Pathology report gross observation Narrative',
  '27898-6': 'Pathology studies (set)',
  '11526-1': 'Pathology study',
  '85847-2': 'Patient Care team information',
  '35090-0': 'Patient history',
  '11383-7': 'Patient problem outcome Narrative',
  '60591-5': 'Patient summary Document',
  '75503-3': "Patient's home Note",
  '84165-0': "Patient's home Plan of care note",
  '34129-7': "Patient's home Progress note",
  '48768-6': 'Payment sources Document',
  '68722-8': 'Pediatric cardiology Note',
  '68896-0': 'Pediatric endocrinology Progress note',
  '68741-8': 'Pediatric gastroenterology Note',
  '68752-5': 'Pediatric hematology-oncology Note',
  '68763-2': 'Pediatric infectious diseases Progress note',
  '68785-5': 'Pediatric pulmonology Progress note',
  '68881-2': 'Pediatric surgery Note',
  '78726-7': 'Pediatrics Consult note',
  '68818-4': 'Pediatrics Note',
  '89879-1': 'Pediatrics Outpatient Note',
  '76642-8': 'Percutaneous coronary intervention study',
  '29752-3': 'Perioperative records',
  '34820-1': 'Pharmacology Consult note',
  '34821-9': 'Pharmacology Note',
  '75469-7': 'Pharmacology Outpatient Note',
  '80569-7': 'Pharmacology Progress note',
  '29545-1': 'Physical findings note',
  '10191-5': 'Physical findings of Abdomen Narrative',
  '10192-3': 'Physical findings of Back Narrative',
  '10193-1': 'Physical findings of Breasts Narrative',
  '11390-2': 'Physical findings of Cardiovascular system Narrative',
  '11392-8': 'Physical findings of Chest wall Narrative',
  '11393-6': 'Physical findings of Ears and Nose and Mouth and Throat Narrative',
  '10197-2': 'Physical findings of Eye Narrative',
  '11401-7': 'Physical findings of Female genitalia Narrative',
  '10210-3': 'Physical findings of General status Narrative',
  '11400-9': 'Physical findings of Genitalia Narrative',
  '10198-0': 'Physical findings of Genitourinary tract Narrative',
  '10199-8': 'Physical findings of Head Narrative',
  '11402-5': 'Physical findings of Male genitalia Narrative',
  '11410-8': 'Physical findings of Musculoskeletal system Narrative',
  '11411-6': 'Physical findings of Neck Narrative',
  '10202-0': 'Physical findings of Nervous system Narrative',
  '10204-6': 'Physical findings of Pelvis Narrative',
  '10205-3': 'Physical findings of Rectum Narrative',
  '100223-7': 'Physical findings of Retina Narrative',
  '10206-1': 'Physical findings of Skin Narrative',
  '10207-9': 'Physical findings of Thorax and Lungs Narrative',
  '34822-7': 'Physical medicine and rehab Consult note',
  '34823-5': 'Physical medicine and rehab Note',
  '83725-2': 'Physical medicine and rehab Outpatient Note',
  '83728-6': 'Physical medicine and rehab procedure note',
  '89030-1': 'Physical medicine and rehab Progress note',
  '34824-3': 'Physical therapy Consult note',
  '34825-0': 'Physical therapy Evaluation note',
  '18735-1': 'Physical therapy Initial evaluation note',
  '28579-1': 'Physical therapy Note',
  '84318-5': 'Physical therapy Outpatient Note',
  '80762-8': 'Physical therapy Plan of care note',
  '11508-9': 'Physical therapy Progress note',
  '22028-5': 'Physician [Identifier]',
  '83805-2': 'Physician Admission evaluation note',
  '11490-0': 'Physician Discharge summary',
  '28568-4': 'Physician Emergency department Note',
  '75476-2': 'Physician Note',
  '83798-9': 'Physician Outpatient Note',
  '11505-5': 'Physician procedure note',
  '18776-5': 'Plan of care note',
  '34826-8': 'Plastic surgery Consult note',
  '34827-6': 'Plastic surgery Note',
  '83852-4': 'Plastic surgery Outpatient Note',
  '68595-8': 'Plastic surgery Progress note',
  '34828-4': 'Podiatry Consult note',
  '34829-2': 'Podiatry Note',
  '75480-4': 'Podiatry Outpatient Note',
  '11509-7': 'Podiatry Progress note',
  '28633-6': 'Polysomnography (sleep) study',
  '93037-0': 'Portable medical order form',
  '36588-2': 'Portable XR Abdomen AP',
  '30726-4': 'Portable XR Cervical spine AP and Lateral',
  '103281-2': 'Portable XR Chest and Abdomen Views',
  '36589-0': 'Portable XR Chest AP single view',
  '30746-2': 'Portable XR Chest Views',
  '24632-2': 'Portable XR Chest Views AP',
  '38796-9': 'Portable XR Hip - right Views',
  '67860-7': 'Postoperative evaluation and management note',
  '64298-3': 'Power of attorney',
  '11449-6': 'Pregnancy status - Reported',
  '90767-5': 'Pregnancy summary Document',
  '67862-3': 'Preoperative evaluation and management note',
  '66149-6': 'Prescribed medications',
  '83871-4': 'Primary care Annual evaluation note',
  '68834-1': 'Primary care Note',
  '57016-8': 'Privacy policy acknowledgment Document',
  '57017-6': 'Privacy policy Organization Document',
  '11450-4': 'Problem list - Reported',
  '59774-0': 'Procedure anesthesia Narrative',
  '59770-8': 'Procedure estimated blood loss Narrative',
  '59776-5': 'Procedure findings Narrative',
  '29554-3': 'Procedure Narrative',
  '28570-0': 'Procedure note',
  '59773-2': 'Procedure specimens taken Narrative',
  '11506-3': 'Progress note',
  '46209-3': 'Provider orders',
  '11492-6': 'Provider-unspecifed',
  '34788-0': 'Psychiatry Consult note',
  '59259-2': 'Psychiatry Discharge summary',
  '28627-8': 'Psychiatry Progress note',
  '11510-5': 'Psychology Progress note',
  '81578-7': 'PT Brain metabolic',
  '34103-2': 'Pulmonary Consult note',
  '58477-1': 'Pulmonary function study',
  '64063-1': 'Pulmonary Hospital Progress note',
  '34830-0': 'Pulmonary Note',
  '83915-9': 'Pulmonary Outpatient Note',
  '80798-2': 'Pulmonary procedure note',
  '80819-6': 'Pulmonary Progress note',
  '27896-0': 'Pulmonary studies (set)',
  '2900-9': 'Pyridoxine [Mass/volume] in Serum or Plasma',
  'LP29684-5': 'RAD',
  '34831-8': 'Radiation oncology Consult note',
  '34832-6': 'Radiation oncology Note',
  '83918-3': 'Radiation oncology Outpatient Note',
  '78656-6': 'Radiation oncology procedure note',
  '78665-7': 'Radiation oncology Progress note',
  '73575-3': 'Radiology Consult note',
  '19005-8': 'Radiology Imaging study [Impression] (narrative)',
  '75490-3': 'Radiology Note',
  '97580-5': 'Radiology Progress note',
  '18726-0': 'Radiology studies (set)',
  '31147-2': 'Reagin Ab [Titer] in Serum by RPR',
  '42349-1': 'Reason for referral (narrative)',
  '29299-5': 'Reason for visit Narrative',
  '78664-0': 'Recreational therapy Progress note',
  '42216-2': 'Referral lab name [Identifier]',
  '19146-0': 'Referral lab test results',
  '57133-1': 'Referral note',
  '64292-6': 'Release of information consent Document',
  '30954-2': 'Relevant diagnostic tests/laboratory data note',
  '80563-0': 'Report',
  '88085-6': 'Reportability response report Document Public health',
  '83937-3': 'Research Outpatient Note',
  '68840-8': 'Research Progress note',
  '77602-1': 'Research study consent',
  '48824-7': 'Respiratory allergen panel',
  '83943-1': 'Respiratory therapy Hospital Note',
  '68479-5': 'Respiratory therapy Hospital Progress note',
  '34838-3': 'Respiratory therapy Note',
  '83945-6': 'Respiratory therapy Outpatient Note',
  '70007-0': 'Restraint note',
  '10187-3': 'Review of systems Narrative - Reported',
  '25068-8': 'RF 1 hour',
  '24574-6': 'RF Biliary ducts and Gallbladder Views during surgery W contrast biliary duct',
  '24575-3': 'RF Biliary ducts and Gallbladder Views W contrast percutaneous transhepatic',
  '30808-0': 'RF Cervical and thoracic and lumbar spine Views W contrast IT',
  '24947-4': 'RF Cervical spine Views W contrast IT',
  '24666-0': 'RF Colon Views W air and barium contrast PR',
  '44227-7': 'RF Colon Views W barium contrast PR',
  '42681-7': 'RF Colon Views W gastrografin PR',
  '30633-2': 'RF Esophagus Views W barium contrast PO',
  '24678-5': 'RF Esophagus Views W contrast PO',
  '24679-3': 'RF Esophagus Views W gastrografin PO',
  '39144-1': 'RF Gastrointestinal tract upper Single view W air contrast PO',
  '24715-5': 'RF Gastrointestinal tract upper Single view W contrast PO',
  '86420-7': 'RF Gastrointestinal tract upper Views W air contrast PO and W barium contrast PO',
  '42459-8': 'RF Gastrointestinal tract upper Views W contrast PO',
  '42684-1': 'RF Gastrointestinal tract upper Views W gastrografin PO',
  '42012-5': 'RF Gastrointestinal tract upper Views W water soluble contrast PO',
  '42702-1': 'RF Greater than 1 hour',
  '35888-7': 'RF Guidance for arthrocentesis of Hip',
  '24771-8': 'RF Guidance for arthrocentesis of Joint space',
  '75669-2': 'RF Guidance for biopsy of Bone marrow',
  '86438-9': 'RF Guidance for check of feeding tube of Gastrointestinal tract upper',
  '30815-5':
    'RF Guidance for endoscopy of Biliary ducts and Pancreatic duct-- W contrast retrograde',
  '24973-0': 'RF Guidance for fluid aspiration of Lumbar spine space',
  '37887-7': 'RF Guidance for fluid aspiration of Pleural space',
  '86400-9': 'RF Guidance for placement of needle in Unspecified body region',
  '30637-3': 'RF Guidance for placement of tube in Gastrointestinal tract',
  '24995-3': 'RF Guidance for placement of tube in Stomach',
  '72549-9': 'RF Guidance for removal of tunneled CV catheter',
  '86407-4': 'RF Guidance of Unspecified body region',
  '26071-1': 'RF Hip - left Arthrogram',
  '26072-9': 'RF Hip - right Arthrogram',
  '86389-4': 'RF Kidney and Ureter and Urinary bladder Views during surgery W contrast retrograde',
  '86388-6': 'RF Kidney and Ureter and Urinary bladder Views W contrast retrograde',
  '42703-9': 'RF Less than 1 hour',
  '24974-8': 'RF Lumbar spine Views W contrast IT',
  '24893-0': 'RF Rectum Single view post contrast PR during defecation',
  '26077-8': 'RF Shoulder - left Arthrogram',
  '26078-6': 'RF Shoulder - right Arthrogram',
  '24910-2': 'RF Shoulder Arthrogram',
  '24911-0': 'RF Shunt Views',
  '24924-3': 'RF Small bowel Views W contrast PO',
  '43574-3': 'RF Upper gastrointestinal tract and Small bowel Views W barium contrast PO',
  '43559-4': 'RF Urinary bladder and Urethra Views W contrast intra bladder during voiding',
  '25020-9': 'RF Urinary bladder and Urethra Views W contrast retrograde via urethra',
  '86401-7': 'RF Urinary bladder Views W contrast intra bladder',
  '25022-5': 'RF Uterus and Fallopian tubes Views W contrast IU',
  '24681-9': 'RF videography Hypopharynx and Esophagus Views',
  '86395-1':
    'RF videography Hypopharynx and Esophagus Views for swallowing function W speech and W barium contrast PO',
  '24682-7':
    'RF videography Hypopharynx and Esophagus Views W liquid and paste contrast PO during swallowing',
  '86396-9': 'RFA Extremity veins - unilateral Views W contrast IV',
  '24615-7': 'RFA Guidance for angioplasty of Carotid artery.intracranial-- W contrast IA',
  '24554-8': 'RFA Guidance for embolization of Artery-- W contrast IA',
  '24625-6': 'RFA Guidance for placement of CV catheter in Vein-- W contrast IV',
  '25024-1': 'RFA Guidance for placement of peripherally-inserted central venous catheter in Vein',
  '25026-6': 'RFA Guidance for placement of venous filter in Inferior vena cava-- W contrast IV',
  '72548-1': 'RFA Guidance for removal of CV catheter from Vein-- W contrast IV',
  '72551-5': 'RFA Guidance for repair of CV catheter without port or pump of Vein',
  '37196-3': 'RFA Lower extremity veins - left Views W contrast IV',
  '37767-1': 'RFA Lower extremity veins - right Views W contrast IV',
  '30816-3': 'RFA Peritoneum Views W contrast percutaneous intraperitoneal',
  '37415-7': 'RFA Upper extremity veins - bilateral Views W contrast IV',
  '38859-5': 'RFA Upper extremity veins - left Views W contrast IV',
  '86419-9': 'RFA Visceral arteries Views W contrast IA',
  '10331-7': 'Rh [Type] in Blood',
  '34839-1': 'Rheumatology Consult note',
  '34840-9': 'Rheumatology Note',
  '83948-0': 'Rheumatology Outpatient Note',
  '78666-5': 'Rheumatology Progress note',
  '9874-9': 'Rhythm segment [Interpretation] Narrative by EKG',
  '25514-1': 'Rubella virus IgG Ab [Presence] in Serum',
  '100156-9': 'SARS-CoV-2 (COVID-19) variant [Type] in Specimen by NAA with probe detection',
  '100350-8': 'Sesamum indicum 1 IgE Ab [Units/volume] in Serum',
  '85866-2': 'Sleep medicine Consult note',
  '85864-7': 'Sleep medicine Note',
  '90711-3': 'Sleep medicine Outpatient Note',
  '97727-2': 'Sleep medicine procedure note',
  '96350-4': 'Sleep medicine Progress note',
  '29762-2': 'Social history note',
  '11519-6': 'Social service Episode of care medical records',
  '34841-7': 'Social worker Consult note',
  '83976-1': 'Social worker Outpatient Note',
  '28656-7': 'Social worker Progress note',
  '19763-2': 'Specimen source [Identifier] in Cervical or vaginal smear or scraping by Cyto stain',
  '31208-2': 'Specimen source identified',
  '39879-2': 'SPECT Bone',
  '39632-5': 'SPECT Brain',
  '39712-5': 'SPECT Heart perfusion',
  '39724-0': 'SPECT Heart perfusion at rest and W stress and W radionuclide IV',
  '39692-9': 'SPECT Liver',
  '39740-6': 'SPECT Parathyroid gland',
  '18740-1': 'Speech-language pathology Initial evaluation note',
  '28571-8': 'Speech-language pathology Note',
  '80782-6': 'Speech-language pathology Plan of care note',
  '11512-1': 'Speech-language pathology Progress note',
  '18759-1': 'Spirometry study',
  '78738-2': 'Sports medicine Consult note',
  '89882-5': 'Sports medicine Outpatient Note',
  '19764-0':
    'Statement of adequacy [Interpretation] of Cervical or vaginal smear or scraping by Cyto stain',
  '47045-0': 'Study report',
  '38269-7': 'Study report Skeletal system DXA',
  '61150-9': 'Subjective Narrative',
  '87626-8': 'Suicide prevention note',
  '67781-5': 'Summarization of encounter note',
  '68608-9': 'Summary note',
  '47046-8': 'Summary of death note',
  '34133-9': 'Summary of episode note',
  '48764-5': 'Summary purpose CCD Document',
  '34847-4': 'Surgery Consult note',
  '78280-5': 'Surgery Education note',
  '78292-0': 'Surgery Flowsheet',
  '34774-0': 'Surgery History and physical note',
  '64057-3': 'Surgery Hospital Progress note',
  '34848-2': 'Surgery Note',
  '84129-6': 'Surgery of the hand Outpatient Note',
  '97576-3': 'Surgery of the hand Progress note',
  '75493-7': 'Surgery Outpatient Note',
  '84021-5': 'Surgery Outpatient procedure note',
  '78322-5': 'Surgery procedure note',
  '34874-8': 'Surgery Surgical operation note',
  '78446-2': 'Surgical critical care Flowsheet',
  '80801-4': 'Surgical oncology Consult note',
  '11504-8': 'Surgical operation note',
  '10218-6': 'Surgical operation note postoperative diagnosis Narrative',
  '10219-4': 'Surgical operation note preoperative diagnosis Narrative',
  '11529-5': 'Surgical pathology study',
  '85208-7': 'Telehealth Consult note',
  '75496-0': 'Telehealth Note',
  '34748-4': 'Telephone encounter Note',
  '34190-9': 'Thermoactinomyces vulgaris IgG Ab [Mass/volume] in Serum',
  '2999-1': 'Thiamine [Mass/volume] in Serum or Plasma',
  '46213-5': 'Tilt table study',
  '66121-5': 'Tissue Pathology biopsy report',
  '18761-7': 'Transfer summary note',
  '3034-6': 'Transferrin [Mass/volume] in Serum or Plasma',
  '84035-5': 'Transplant surgery Consult note',
  '70238-1': 'Transplant surgery Hospital Progress note',
  '68848-1': 'Transplant surgery Note',
  '84036-3': 'Transplant surgery Outpatient Note',
  '78732-5': 'Trauma Consult note',
  '89036-8': 'Trauma Note',
  '90708-9': 'Trauma Outpatient Note',
  '89817-1': 'Trauma procedure note',
  '89816-3': 'Trauma Progress note',
  '75500-9': 'Triage note',
  '85521-3': 'Undersea and hyperbaric medicine Note',
  '101043-8': 'Urgent care center Consult note',
  '75504-1': 'Urgent care center Note',
  '11279-7': 'Urine sediment comments by Light microscopy Narrative',
  '34851-6': 'Urology Consult note',
  '34852-4': 'Urology Note',
  '83598-3': 'Urology Nurse Outpatient Note',
  '75501-7': 'Urology Outpatient Note',
  '84044-7': 'Urology Outpatient procedure note',
  '78659-0': 'Urology procedure note',
  '75220-4': 'Urology Progress note',
  '24558-9': 'US Abdomen',
  '30704-1': 'US Abdomen limited',
  '24532-4': 'US Abdomen RUQ',
  '69276-4': 'US Abdominal Aorta',
  '79374-5': 'US Abdominal Aorta for screening',
  '24548-0': 'US Appendix',
  '39040-1': 'US AV fistula',
  '72529-1': 'US Axilla - left',
  '72528-3': 'US Axilla - right',
  '38021-2': 'US Biliary ducts and Gallbladder',
  '24601-7': 'US Breast',
  '26214-7': 'US Breast - bilateral',
  '26286-5': 'US Breast - bilateral limited',
  '26215-4': 'US Breast - left',
  '26288-1': 'US Breast - left limited',
  '26216-2': 'US Breast - right',
  '26290-7': 'US Breast - right limited',
  '24599-3': 'US Breast limited',
  '24616-5': 'US Carotid arteries',
  '26217-0': 'US Carotid arteries - bilateral',
  '24630-6': 'US Chest',
  '69281-4': 'US Chest limited',
  '38016-2': 'US Chest wall',
  '24693-4': 'US Extremity',
  '26225-3': 'US Extremity - left',
  '62451-0': 'US Extremity - left limited',
  '26230-3': 'US Extremity - right',
  '62452-8': 'US Extremity - right limited',
  '41816-0': 'US Extremity veins - right',
  '80834-5': 'US for multiple gestation pregnancy in first trimester',
  '80833-7': 'US for multiple gestation pregnancy limited',
  '11525-3': 'US for pregnancy',
  '80869-1': 'US for pregnancy in first trimester',
  '80866-7': 'US for pregnancy in second or third trimester',
  '80870-9': 'US for pregnancy limited',
  '39032-8': 'US for transplanted kidney',
  '24711-4': 'US Gallbladder',
  '24719-7': 'US Groin',
  '42450-7': 'US Guidance for aspiration of cyst of Breast - left',
  '30698-5': 'US Guidance for aspiration of cyst of Unspecified body region',
  '37914-9': 'US Guidance for biopsy of Breast',
  '42449-9': 'US Guidance for biopsy of Breast - left',
  '42457-2': 'US Guidance for biopsy of Breast - right',
  '24772-6': 'US Guidance for biopsy of Kidney',
  '26341-8': 'US Guidance for biopsy of Kidney - left',
  '26342-6': 'US Guidance for biopsy of Kidney - right',
  '24816-1': 'US Guidance for biopsy of Liver',
  '25009-2': 'US Guidance for biopsy of Thyroid gland',
  '25059-7': 'US Guidance for biopsy of Unspecified body region',
  '46285-3': 'US Guidance for core needle biopsy of Thyroid gland',
  '24559-7': 'US Guidance for drainage and placement of drainage catheter of Abdomen',
  '30699-3':
    'US Guidance for drainage and placement of drainage catheter of Unspecified body region',
  '44160-0': 'US Guidance for fine needle aspiration of Breast',
  '38019-6': 'US Guidance for fine needle aspiration of Thyroid gland',
  '38018-8': 'US Guidance for fine needle aspiration of Unspecified body region',
  '24662-9': 'US Guidance for fluid aspiration of Pleural space',
  '72530-9': 'US Guidance for injection of Joint',
  '43760-8': 'US Guidance for localization of Breast - right',
  '24600-9': 'US Guidance for needle localization of Breast',
  '26314-5': 'US Guidance for needle localization of Breast - left',
  '26318-6': 'US Guidance for needle localization of Breast - right',
  '39522-8': 'US guidance for percutaneous biopsy of Lymph node',
  '38023-8': 'US Guidance for percutaneous biopsy.core needle of Breast - left',
  '38025-3': 'US Guidance for percutaneous biopsy.core needle of Breast - right',
  '64993-9': 'US Guidance for placement of needle in Unspecified body region',
  '42140-4': 'US Guidance for placement of tube in Chest',
  '24731-2': 'US Head',
  '44164-2': 'US Head and Neck',
  '80858-4': 'US Head and neck soft tissue',
  '42148-7': 'US Heart',
  '42707-0': 'US Heart limited',
  '85475-2': 'US Heart Transesophageal',
  '59281-6': 'US Heart Transthoracic',
  '26250-1': 'US Hip - bilateral',
  '38036-0': 'US Kidney',
  '43774-9': 'US Kidney - bilateral',
  '69402-6': 'US Kidney - bilateral and Urinary bladder',
  '38038-6': 'US Kidney - left',
  '38035-2': 'US Kidney limited',
  '28614-6': 'US Liver',
  '30709-0': 'US Lower extremity',
  '38040-2': 'US Lower extremity - left',
  '38039-4': 'US Lower extremity - left limited',
  '38051-9': 'US Lower extremity - right',
  '38050-1': 'US Lower extremity - right limited',
  '48693-6': 'US Lower extremity artery',
  '38130-1': 'US Lower extremity artery - bilateral',
  '41834-3': 'US Lower extremity artery - left',
  '41815-2': 'US Lower extremity artery - right',
  '80861-8': 'US Lower extremity soft tissue',
  '46363-8': 'US Lower extremity vein',
  '46364-6': 'US Lower extremity vein - bilateral',
  '48692-8': 'US Lower extremity vein - left',
  '48691-0': 'US Lower extremity vein - right',
  '69385-3': 'US Lower extremity veins - bilateral',
  '69392-9': 'US Lower extremity veins - left',
  '69394-5': 'US Mesenteric arteries',
  '24842-7': 'US Neck',
  '24869-0': 'US Pelvis',
  '38046-9': 'US Pelvis limited',
  '42455-6': 'US Pelvis transabdominal and transvaginal',
  '24677-7': 'US Pelvis transvaginal',
  '24884-9': 'US Prostate transrectal',
  '24531-6': 'US Retroperitoneum',
  '38047-7': 'US Retroperitoneum limited',
  '25002-7': 'US Scrotum and testicle',
  '26271-7': 'US Scrotum and Testicle - bilateral',
  '24990-4': 'US Spleen',
  '24547-2': 'US Thoracic and abdominal aorta',
  '38011-3': 'US Thoracic and abdominal aorta limited',
  '25010-0': 'US Thyroid gland',
  '80867-5': 'US transabdominal and transvaginal for pregnancy in first trimester',
  '80865-9': 'US transvaginal for pregnancy',
  '25061-3': 'US Unspecified body region',
  '30701-7': 'US Unspecified body region during surgery',
  '38041-0': 'US Upper extremity - left',
  '38052-7': 'US Upper extremity - right',
  '48448-5': 'US Upper extremity artery',
  '38014-7': 'US Upper extremity artery - bilateral',
  '41833-5': 'US Upper extremity artery - left',
  '41814-5': 'US Upper extremity artery - right',
  '80881-6': 'US Upper extremity soft tissue',
  '48690-2': 'US Upper extremity vein - bilateral',
  '48689-4': 'US Upper extremity vein - left',
  '48688-6': 'US Upper extremity vein - right',
  '69395-2': 'US Upper extremity veins',
  '25019-1': 'US Urinary bladder',
  '42143-8': 'US Uterus and Fallopian tubes W saline IU',
  '24534-0': 'US.doppler Abdominal vessels',
  '43572-7': 'US.doppler Abdominal vessels limited',
  '80897-2': 'US.doppler Aorta and Iliac artery - bilateral limited',
  '42146-1': 'US.doppler Carotid arteries',
  '43765-7': 'US.doppler Carotid arteries - bilateral',
  '39427-0': 'US.doppler Carotid arteries - left',
  '39437-9': 'US.doppler Carotid arteries -right',
  '80850-1': 'US.doppler Carotid arteries limited',
  '39418-9': 'US.doppler Extremity vein - bilateral',
  '39429-6': 'US.doppler Extremity vein - left',
  '80863-4': 'US.doppler for transplanted kidney',
  '24733-8': 'US.doppler Head vessels',
  '81161-2': 'US.doppler Heart for blood flow',
  '39421-3': 'US.doppler Lower extremity artery - bilateral',
  '39499-9': 'US.doppler Lower extremity artery - left',
  '39505-3': 'US.doppler Lower extremity artery - right',
  '39420-5': 'US.doppler Lower extremity vein - bilateral',
  '39432-0': 'US.doppler Lower extremity vein - left',
  '39443-7': 'US.doppler Lower extremity vein - right',
  '24870-8': 'US.doppler Pelvis vessels',
  '39435-3': 'US.doppler Renal artery',
  '39426-2': 'US.doppler Renal vessels',
  '48742-1': 'US.doppler Scrotum and testicle',
  '94681-4': 'US.doppler Thoracic and Abdominal Aorta and Inferior Vena Cava and Illiac vessels',
  '94680-6':
    'US.doppler Thoracic and Abdominal Aorta and Inferior Vena Cava and Illiac vessels limited',
  '39508-7': 'US.doppler Umbilical vessels Fetal',
  '39423-9': 'US.doppler Upper extremity artery - bilateral',
  '39500-4': 'US.doppler Upper extremity artery - left',
  '39506-1': 'US.doppler Upper extremity artery - right',
  '39496-5': 'US.doppler Upper extremity vein - bilateral',
  '39501-2': 'US.doppler Upper extremity vein - left',
  '39507-9': 'US.doppler Upper extremity vein - right',
  '39525-1': 'US.doppler Vein',
  '72055-7': 'Vascular access panel [ESRD]',
  '34853-2': 'Vascular surgery Consult note',
  '34854-0': 'Vascular surgery Outpatient Note',
  '84053-8': 'Vascular surgery procedure note',
  '85441-4': 'Vascular surgery Progress note',
  '78243-3': 'Vascular surgery Surgical operation note',
  '8716-3': 'Vital signs note',
  '59283-2': 'Well child visit note',
  '85869-6': 'Womens health Education note',
  '85858-9': 'Womens health Outpatient Note',
  '93954-6': 'Wound',
  '84303-7': 'Wound care management Consult note',
  '46215-0': 'Wound care management Note',
  '84302-9': 'Wound care management Nurse Note',
  '96352-0': 'Wound care management Outpatient Note',
  '96353-8': 'Wound care management procedure note',
  '89821-3': 'Wound care management Progress note',
  '101336-6': 'Wr sup(b) Ag [Presence] on Red Blood Cells',
  '36641-9': 'XR Abdomen 2 Views',
  '36293-9': 'XR Abdomen 3 Views',
  '24799-9': 'XR Abdomen AP',
  '79350-5': 'XR Abdomen GE 3 Views',
  '83016-6': 'XR Abdomen GE 3 Views AP and Oblique and Cone',
  '36550-2': 'XR Abdomen Single view',
  '24798-1': 'XR Abdomen Supine and Upright',
  '42019-0': 'XR Abdomen upright and left lateral decubitus',
  '37636-8': 'XR Abdomen Views',
  '37580-8': 'XR Acromioclavicular joint - bilateral Views WO and W weight',
  '36644-3': 'XR and RF Chest 2 Views and Views',
  '26385-5': 'XR Ankle - bilateral 2 Views',
  '36684-9': 'XR Ankle - bilateral AP and Lateral',
  '48480-8': 'XR Ankle - bilateral GE 3 Views',
  '26097-6': 'XR Ankle - bilateral Views',
  '26386-3': 'XR Ankle - left 2 Views',
  '36296-2': 'XR Ankle - left 3 Views',
  '36685-6': 'XR Ankle - left AP and Lateral',
  '46390-1': 'XR Ankle - left GE 3 Views',
  '69307-7': 'XR Ankle - left Single view',
  '26098-4': 'XR Ankle - left Views',
  '26387-1': 'XR Ankle - right 2 Views',
  '37665-7': 'XR Ankle - right 3 Views',
  '37667-3': 'XR Ankle - right AP and Lateral',
  '37668-1': 'XR Ankle - right AP and Lateral and oblique',
  '46347-1': 'XR Ankle - right GE 3 Views',
  '69314-3': 'XR Ankle - right Single view',
  '26099-2': 'XR Ankle - right Views',
  '24540-7': 'XR Ankle 2 Views',
  '51395-2': 'XR Ankle and Foot - left Views',
  '24541-5': 'XR Ankle Views',
  '37362-1': 'XR Bones Bone age Views',
  '38089-9': 'XR Bones Limited Survey Views for metastasis',
  '43518-0': 'XR Bones Survey Views',
  '37365-4': 'XR Bones Survey Views for metastasis',
  '36662-5': 'XR Calcaneus - left 2 Views',
  '26101-6': 'XR Calcaneus - left Views',
  '37718-4': 'XR Calcaneus - right 2 Views',
  '26102-4': 'XR Calcaneus - right Views',
  '39067-4': 'XR Cervical and thoracic and lumbar spine AP and Lateral',
  '83028-1': 'XR Cervical and thoracic and lumbar spine GE 2 Views',
  '38008-9': 'XR Cervical and thoracic and lumbar spine Views',
  '43539-6': 'XR Cervical spine 2 or 3 Views',
  '36669-0': 'XR Cervical spine 2 Views',
  '24941-7': 'XR Cervical spine 3 Views',
  '83023-2': 'XR Cervical spine 4 or 5 Views',
  '36331-7': 'XR Cervical spine 4 Views',
  '24939-1': 'XR Cervical spine 5 Views',
  '24942-5': 'XR Cervical spine AP and Lateral',
  '37103-9': 'XR Cervical spine AP and Lateral and Odontoid',
  '37100-5':
    'XR Cervical spine AP and Oblique and Odontoid and (Lateral W flexion and W extension)',
  '79351-3': 'XR Cervical spine GE 2 Views',
  '44212-9': 'XR Cervical spine GE 4 Views',
  '79352-1': 'XR Cervical spine GE 6 Views',
  '24943-3': 'XR Cervical spine Lateral',
  '37133-6': 'XR Cervical spine Lateral Views W flexion and W extension',
  '42710-4': 'XR Cervical spine Limited Views',
  '37139-3': 'XR Cervical spine Oblique and (Lateral W flexion and W extension)',
  '24940-9': 'XR Cervical spine Single view',
  '24946-6': 'XR Cervical spine Views',
  '42164-4': 'XR Cervical spine Views and Oblique',
  '24945-8': 'XR Cervical spine Views W flexion and W extension',
  '36643-5': 'XR Chest 2 Views',
  '36298-8': 'XR Chest 3 Views',
  '42699-9': 'XR Chest and Abdomen Single view',
  '42269-1': 'XR Chest and Abdomen Views',
  '36572-6': 'XR Chest AP',
  '36687-2': 'XR Chest AP and Lateral',
  '24637-1': 'XR Chest AP left lateral-decubitus',
  '24640-5': 'XR Chest Apical lordotic',
  '44211-1': 'XR Chest GE 4 Views',
  '43463-9': 'XR Chest PA and Abdomen Supine and Upright',
  '42272-5': 'XR Chest PA and Lateral',
  '37143-5': 'XR Chest PA and Lateral and AP lateral-decubitus',
  '30744-7': 'XR Chest PA and Lateral and Oblique',
  '24648-8': 'XR Chest PA upright',
  '24650-4': 'XR Chest Right lateral-decubitus and Left lateral-decubitus',
  '36554-4': 'XR Chest Single view',
  '30745-4': 'XR Chest Views',
  '30736-3': 'XR Chest Views W inspiration and expiration',
  '26106-5': 'XR Clavicle - bilateral Views',
  '26107-3': 'XR Clavicle - left Views',
  '26108-1': 'XR Clavicle - right Views',
  '30883-3': 'XR Coccyx Views',
  '36649-2': 'XR Elbow - bilateral 2 Views',
  '48481-6': 'XR Elbow - bilateral GE 3 Views',
  '36650-0': 'XR Elbow - left 2 Views',
  '36301-0': 'XR Elbow - left 3 Views',
  '36691-4': 'XR Elbow - left AP and Lateral',
  '46344-8': 'XR Elbow - left GE 3 Views',
  '26110-7': 'XR Elbow - left Views',
  '37681-4': 'XR Elbow - right 2 Views',
  '37682-2': 'XR Elbow - right 3 Views',
  '37684-8': 'XR Elbow - right AP and Lateral',
  '46345-5': 'XR Elbow - right GE 3 Views',
  '26111-5': 'XR Elbow - right Views',
  '36648-4': 'XR Elbow 2 Views',
  '36689-8': 'XR Elbow AP and Lateral',
  '24676-9': 'XR Elbow Views',
  '37198-9': 'XR Esophagus Views W contrast PO',
  '44199-8': 'XR Facial bones 1 or 2 Views',
  '48479-0': 'XR Facial bones GE 3 Views',
  '24695-9': 'XR Facial bones Views',
  '36653-4': 'XR Femur - bilateral 2 Views',
  '36694-8': 'XR Femur - bilateral AP and Lateral',
  '26118-0': 'XR Femur - bilateral Views',
  '36654-2': 'XR Femur - left 2 Views',
  '36695-5': 'XR Femur - left AP and Lateral',
  '36560-1': 'XR Femur - left Single view',
  '26120-6': 'XR Femur - left Views',
  '37690-5': 'XR Femur - right 2 Views',
  '37692-1': 'XR Femur - right AP and Lateral',
  '37689-7': 'XR Femur - right Single view',
  '26122-2': 'XR Femur - right Views',
  '24700-7': 'XR Femur and Tibia Views for leg length',
  '36656-7': 'XR Finger - left 2 Views',
  '26125-5': 'XR Finger - left Views',
  '37694-7': 'XR Finger - right 2 Views',
  '26126-3': 'XR Finger - right Views',
  '37518-8': 'XR Finger fifth - left Views',
  '38147-5': 'XR Finger fifth - right Views',
  '37520-4': 'XR Finger fourth - left Views',
  '38146-7': 'XR Finger fourth - right Views',
  '37522-0': 'XR Finger second - left Views',
  '38144-2': 'XR Finger second - right Views',
  '37524-6': 'XR Finger third - left Views',
  '38145-9': 'XR Finger third - right Views',
  '24706-4': 'XR Finger Views',
  '36657-5': 'XR Foot - bilateral 2 Views',
  '36306-9': 'XR Foot - bilateral 3 Views',
  '36696-3': 'XR Foot - bilateral AP and Lateral',
  '48478-2': 'XR Foot - bilateral GE 3 Views',
  '26127-1': 'XR Foot - bilateral Views',
  '38846-2': 'XR Foot - left 2 Views',
  '36307-7': 'XR Foot - left 3 Views',
  '36697-1': 'XR Foot - left AP and Lateral',
  '36730-0': 'XR Foot - left AP and Lateral and oblique',
  '48477-4': 'XR Foot - left GE 3 Views',
  '26128-9': 'XR Foot - left Views',
  '37697-0': 'XR Foot - right 2 Views',
  '37699-6': 'XR Foot - right 3 Views',
  '37701-0': 'XR Foot - right AP and Lateral',
  '37702-8': 'XR Foot - right AP and Lateral and oblique',
  '48476-6': 'XR Foot - right GE 3 Views',
  '69316-8': 'XR Foot - right Single view',
  '26129-7': 'XR Foot - right Views',
  '44188-1': 'XR Foot GE 3 Views',
  '24709-8': 'XR Foot Views',
  '37533-7': 'XR Great toe - left Views',
  '38152-5': 'XR Great toe - right Views',
  '46371-1': 'XR Guidance for change of percutaneous tube in Unspecified body region-- W contrast',
  '26388-9': 'XR Hand - bilateral 2 Views',
  '26379-8': 'XR Hand - bilateral 3 Views',
  '103245-7': 'XR Hand - bilateral GE 3 Views',
  '36629-4': 'XR Hand - bilateral Views',
  '26389-7': 'XR Hand - left 2 Views',
  '26380-6': 'XR Hand - left 3 Views',
  '47370-2': 'XR Hand - left GE 3 Views',
  '36630-2': 'XR Hand - left Views',
  '26390-5': 'XR Hand - right 2 Views',
  '26381-4': 'XR Hand - right 3 Views',
  '37710-1': 'XR Hand - right AP and Lateral',
  '47371-0': 'XR Hand - right GE 3 Views',
  '37716-8': 'XR Hand - right Views',
  '79357-0': 'XR Hand GE 3 Views',
  '28582-5': 'XR Hand Views',
  '69058-6': 'XR Hip - bilateral 2 Views',
  '36308-5': 'XR Hip - bilateral 3 Views',
  '26400-2': 'XR Hip - bilateral Single view',
  '26130-5': 'XR Hip - bilateral Views',
  '103242-4': 'XR Hip - left 2 or 3 Views',
  '36664-1': 'XR Hip - left 2 Views',
  '36309-3': 'XR Hip - left 3 Views',
  '79359-6': 'XR Hip - left GE 2 Views',
  '26401-0': 'XR Hip - left Single view',
  '38065-9': 'XR Hip - left Single view during surgery',
  '26131-3': 'XR Hip - left Views',
  '103238-2': 'XR Hip - right 2 or 3 Views',
  '37721-8': 'XR Hip - right 2 Views',
  '37722-6': 'XR Hip - right 3 Views',
  '79360-4': 'XR Hip - right GE 2 Views',
  '26402-8': 'XR Hip - right Single view',
  '38818-1': 'XR Hip - right Single view during surgery',
  '26132-1': 'XR Hip - right Views',
  '47372-8': 'XR Hip Views during surgery',
  '26392-1': 'XR Humerus - left 2 Views',
  '36632-8': 'XR Humerus - left Views',
  '26393-9': 'XR Humerus - right 2 Views',
  '37738-2': 'XR Humerus - right Views',
  '43485-2': 'XR Kidney Views during surgery W contrast retrograde',
  '37607-9': 'XR Kidney Views W contrast IV',
  '38105-3': 'XR Kidney Views W contrast retrograde',
  '103230-9': 'XR Knee - bilateral 1 or 2 Views',
  '26394-7': 'XR Knee - bilateral 2 Views',
  '36310-1': 'XR Knee - bilateral 3 Views',
  '36325-9': 'XR Knee - bilateral 4 Views',
  '36590-8': 'XR Knee - bilateral AP and Lateral',
  '26358-2': 'XR Knee - bilateral AP W standing',
  '103253-1': 'XR Knee - bilateral GE 4 Views',
  '36635-1': 'XR Knee - bilateral Views',
  '47373-6': 'XR Knee - left 1 or 2 Views',
  '26395-4': 'XR Knee - left 2 Views',
  '36311-9': 'XR Knee - left 3 Views',
  '36326-7': 'XR Knee - left 4 Views',
  '36710-2': 'XR Knee - left AP and Lateral',
  '36733-4': 'XR Knee - left AP and Lateral and oblique',
  '43498-5': 'XR Knee - left GE 3 Views',
  '47374-4': 'XR Knee - left GE 4 Views',
  '36567-6': 'XR Knee - left Single view',
  '36636-9': 'XR Knee - left Views',
  '47375-1': 'XR Knee - right 1 or 2 Views',
  '26396-2': 'XR Knee - right 2 Views',
  '37742-4': 'XR Knee - right 3 Views',
  '37743-2': 'XR Knee - right 4 Views',
  '37745-7': 'XR Knee - right AP and Lateral',
  '37748-1': 'XR Knee - right AP and Lateral and oblique',
  '43482-9': 'XR Knee - right GE 3 Views',
  '47376-9': 'XR Knee - right GE 4 Views',
  '37741-6': 'XR Knee - right Single view',
  '37758-0': 'XR Knee - right Views',
  '26087-7': 'XR Knee - right Views W standing',
  '44198-0': 'XR Knee 1 or 2 Views',
  '30788-4': 'XR Knee 3 Views',
  '30789-2': 'XR Knee 4 Views',
  '24807-0': 'XR Knee AP W standing',
  '79364-6': 'XR Knee GE 4 Views',
  '24579-5': 'XR Long bones Survey Views',
  '26113-1': 'XR Lower extremity - left Views',
  '26114-9': 'XR Lower extremity - right Views',
  '42411-9':
    'XR Lumbar spine (AP^W R-bending and W L-bending) and (Lateral^W flexion and W extension)',
  '48469-1': 'XR Lumbar spine 2 or 3 Views',
  '36670-8': 'XR Lumbar spine 2 Views',
  '30775-1': 'XR Lumbar spine 3 Views',
  '103255-6': 'XR Lumbar spine 4 or 5 Views',
  '36332-5': 'XR Lumbar spine 4 Views',
  '30797-5': 'XR Lumbar spine 5 Views',
  '30777-7': 'XR Lumbar spine AP',
  '24970-6': 'XR Lumbar spine AP and Lateral',
  '36735-9': 'XR Lumbar spine AP and Lateral and oblique',
  '37105-4': 'XR Lumbar spine AP and Lateral and Spot',
  '47382-7': 'XR Lumbar spine GE 4 Views',
  '24969-8': 'XR Lumbar spine Lateral',
  '43791-3': 'XR Lumbar spine Oblique Views',
  '30773-6': 'XR Lumbar spine Single view',
  '24972-2': 'XR Lumbar spine Views',
  '24971-4': 'XR Lumbar spine Views W flexion and W extension',
  '42413-5': 'XR Lumbar spine Views W right bending and W left bending',
  '47379-3': 'XR Mandible GE 4 Views',
  '47380-1': 'XR Mandible LE 3 Views',
  '24829-4': 'XR Mandible Views',
  '37604-6': 'XR Nasal bones 3 Views',
  '103243-2': 'XR Nasal bones GE 3 Views',
  '24834-4': 'XR Nasal bones Views',
  '42438-2': 'XR Neck AP and Lateral',
  '24843-5': 'XR Neck Lateral',
  '37639-2': 'XR Neck Views',
  '48747-0': 'XR Orbit - bilateral GE 4 Views',
  '24854-2': 'XR Orbit - bilateral Views',
  '30720-7': 'XR Orbit - bilateral Views for foreign body',
  '36886-0': 'XR Orbit Views',
  '44208-7': 'XR Orbit Views for foreign body',
  '43522-2': 'XR Pelvis 1 or 2 Views',
  '37617-8': 'XR Pelvis 2 Views',
  '103252-3': 'XR Pelvis and Hip - bilateral View AP and View 2',
  '103274-7': 'XR Pelvis and Hip - bilateral View AP and Views 3 or 4',
  '30768-6': 'XR Pelvis and Hip - bilateral Views',
  '103235-8': 'XR Pelvis and Hip - left 2 or 3 Views',
  '42685-8': 'XR Pelvis and Hip - left 2 Views',
  '103270-5': 'XR Pelvis and Hip - left Single view',
  '103249-9': 'XR Pelvis and Hip - left View AP and Views 2 or 3',
  '36631-0': 'XR Pelvis and Hip - left Views',
  '103234-1': 'XR Pelvis and Hip - right 2 or 3 Views',
  '42686-6': 'XR Pelvis and Hip - right 2 Views',
  '103258-0': 'XR Pelvis and Hip - right Single view',
  '103233-3': 'XR Pelvis and Hip - right View AP and Views 2 or 3',
  '38771-2': 'XR Pelvis and Hip - right Views',
  '30767-8': 'XR Pelvis and Hip Views',
  '37622-8': 'XR Pelvis AP',
  '83031-5': 'XR Pelvis AP and Hip - bilateral GE 2 Views',
  '43543-8': 'XR Pelvis GE 3 Views',
  '37616-0': 'XR Pelvis Single view',
  '28561-9': 'XR Pelvis Views',
  '39360-3': 'XR Pelvis Views and Inlet and Outlet',
  '36659-1': 'XR Radius and Ulna - bilateral 2 Views',
  '36699-7': 'XR Radius and Ulna - bilateral AP and Lateral',
  '36660-9': 'XR Radius and Ulna - left 2 Views',
  '36700-3': 'XR Radius and Ulna - left AP and Lateral',
  '100760-8': 'XR Radius and Ulna - left Single view',
  '26148-7': 'XR Radius and Ulna - left Views',
  '37707-7': 'XR Radius and Ulna - right 2 Views',
  '37708-5': 'XR Radius and Ulna - right AP and Lateral',
  '26150-3': 'XR Radius and Ulna - right Views',
  '36313-5': 'XR Ribs - bilateral 3 Views',
  '39099-7': 'XR Ribs - bilateral 4 Views and Chest PA',
  '69071-9': 'XR Ribs - bilateral and Chest Views',
  '48485-7': 'XR Ribs - bilateral GE 3 Views and Chest PA',
  '26151-1': 'XR Ribs - bilateral Views',
  '41811-1': 'XR Ribs - bilateral Views and Chest PA',
  '37066-8': 'XR Ribs - left 2 Views',
  '39326-4': 'XR Ribs - left and Chest Views',
  '48486-5': 'XR Ribs - left GE 3 Views and Chest PA',
  '26152-9': 'XR Ribs - left Views',
  '41832-7': 'XR Ribs - left Views and Chest PA',
  '37780-4': 'XR Ribs - right 2 Views',
  '37781-2': 'XR Ribs - right 3 Views',
  '48484-0': 'XR Ribs - right GE 3 Views and Chest PA',
  '26153-7': 'XR Ribs - right Views',
  '42010-9': 'XR Ribs - right Views and Chest PA',
  '24899-7': 'XR Ribs Views',
  '48467-5': 'XR Sacroiliac Joint 1 or 2 Views',
  '37648-3': 'XR Sacroiliac Joint 3 Views',
  '44189-9': 'XR Sacroiliac Joint GE 3 Views',
  '24900-3': 'XR Sacroiliac Joint Views',
  '44179-0': 'XR Sacrum and Coccyx 2 Views',
  '79367-9': 'XR Sacrum and Coccyx GE 2 Views',
  '24665-2': 'XR Sacrum and Coccyx Views',
  '26155-2': 'XR Scapula - left Views',
  '26156-0': 'XR Scapula - right Views',
  '36667-4': 'XR Shoulder - bilateral 2 Views',
  '36329-1': 'XR Shoulder - bilateral 4 Views',
  '103257-2': 'XR Shoulder - bilateral GE 3 Views',
  '36568-4': 'XR Shoulder - bilateral Single view',
  '26157-8': 'XR Shoulder - bilateral Views',
  '36668-2': 'XR Shoulder - left 2 Views',
  '26383-0': 'XR Shoulder - left 3 Views',
  '36330-9': 'XR Shoulder - left 4 Views',
  '36677-3': 'XR Shoulder - left 5 Views',
  '79368-7': 'XR Shoulder - left GE 2 Views',
  '103259-8': 'XR Shoulder - left GE 3 Views',
  '36569-2': 'XR Shoulder - left Single view',
  '26158-6': 'XR Shoulder - left Views',
  '37793-7': 'XR Shoulder - right 2 Views',
  '26384-8': 'XR Shoulder - right 3 Views',
  '37794-5': 'XR Shoulder - right 4 Views',
  '37795-2': 'XR Shoulder - right 5 Views',
  '79369-5': 'XR Shoulder - right GE 2 Views',
  '37792-9': 'XR Shoulder - right Single view',
  '26159-4': 'XR Shoulder - right Views',
  '79370-3': 'XR Shoulder GE 2 Views',
  '30748-8': 'XR Shoulder Single view',
  '43523-0': 'XR Sinuses 1 or 2 Views',
  '43486-0': 'XR Sinuses GE 3 Views',
  '24916-9': 'XR Sinuses Views',
  '37867-9': 'XR Skull 2 Views',
  '48487-3': 'XR Skull GE 4 Views',
  '43470-4': 'XR Skull LE 3 Views',
  '28564-3': 'XR Skull Views',
  '103228-3': 'XR Spine Lumbar and Sacrum 2 or 3 Views',
  '79371-1': 'XR Spine Lumbar and Sacrum GE 2 Views',
  '79372-9': 'XR Spine Lumbar and Sacrum GE 4 Views',
  '94684-8': 'XR Spine Lumbar and Sacrum GE 6 Views W right bending and W left bending',
  '103260-6': 'XR Spine Lumbar and Sacrum Views W right bending and W left bending',
  '37875-2': 'XR Spine Single view',
  '69273-1': 'XR Spine thoracolumbar junction 2 Views',
  '37974-3': 'XR Spine thoracolumbar junction AP and Lateral',
  '37975-0': 'XR Spine thoracolumbar junction Views',
  '28613-8': 'XR Spine Views',
  '83047-1': 'XR Sternum GE 2 Views',
  '24994-6': 'XR Sternum Views',
  '72876-6': 'XR Surgical specimen Views',
  '37546-9': 'XR Temporomandibular joint - bilateral Open and Closed mouth',
  '48491-5': 'XR Temporomandibular joint - left Open and Closed mouth',
  '37658-2': 'XR Thoracic and lumbar spine 2 Views for scoliosis',
  '30715-7': 'XR Thoracic and lumbar spine AP and lateral for scoliosis',
  '44206-1': 'XR Thoracic and lumbar spine for scoliosis single view',
  '24930-0': 'XR Thoracic and lumbar spine Views for scoliosis',
  '30717-3': 'XR Thoracic and lumbar spine Views for scoliosis W standing',
  '37905-7': 'XR Thoracic spine 2 Views',
  '37906-5': 'XR Thoracic spine 3 Views',
  '37907-3': 'XR Thoracic spine 4 Views',
  '30752-0': 'XR Thoracic spine AP',
  '30753-8': 'XR Thoracic spine AP and Lateral',
  '38009-7': 'XR Thoracic spine AP and Lateral and Swimmers',
  '37904-0': 'XR Thoracic spine Single view',
  '24983-9': 'XR Thoracic spine Views',
  '39412-2': 'XR Thoracic spine Views and Swimmers',
  '26161-0': 'XR Thumb - left Views',
  '26162-8': 'XR Thumb - right Views',
  '36671-6': 'XR Tibia and Fibula - bilateral 2 Views',
  '36717-7': 'XR Tibia and Fibula - bilateral AP and Lateral',
  '26163-6': 'XR Tibia and Fibula - bilateral Views',
  '36672-4': 'XR Tibia and Fibula - left 2 Views',
  '36718-5': 'XR Tibia and Fibula - left AP and Lateral',
  '69313-5': 'XR Tibia and Fibula - left Single view',
  '26164-4': 'XR Tibia and Fibula - left Views',
  '37815-8': 'XR Tibia and Fibula - right 2 Views',
  '37816-6': 'XR Tibia and Fibula - right AP and Lateral',
  '26165-1': 'XR Tibia and Fibula - right Views',
  '37530-3': 'XR Toe fifth - left Views',
  '38151-7': 'XR Toe fifth - right Views',
  '38150-9': 'XR Toe fourth - right Views',
  '37534-5': 'XR Toe second - left Views',
  '38148-3': 'XR Toe second - right Views',
  '38149-1': 'XR Toe third - right Views',
  '26167-7': 'XR Toes - left Views',
  '37821-6': 'XR Toes - right 2 Views',
  '26168-5': 'XR Toes - right Views',
  '24828-6': 'XR tomography Mandible Panoramic',
  '43468-8': 'XR Unspecified body region Views',
  '43469-6': 'XR Unspecified body region Views for foreign body',
  '42160-2': 'XR Unspecified body region Views for shunt patency',
  '26116-4': 'XR Upper extremity - left Views',
  '26117-2': 'XR Upper extremity - right Views',
  '37482-7': 'XR Wrist - bilateral 2 Views',
  '48483-2': 'XR Wrist - bilateral GE 3 Views',
  '26169-3': 'XR Wrist - bilateral Views',
  '37483-5': 'XR Wrist - left 2 Views',
  '37455-3': 'XR Wrist - left 3 Views',
  '98339-5': 'XR Wrist - left AP',
  '38860-3': 'XR Wrist - left AP and Lateral',
  '46346-3': 'XR Wrist - left GE 3 Views',
  '37302-7': 'XR Wrist - left Scaphoid Views',
  '26170-1': 'XR Wrist - left Views',
  '37826-5': 'XR Wrist - right 2 Views',
  '37827-3': 'XR Wrist - right 3 Views',
  '37832-3': 'XR Wrist - right AP and Lateral',
  '46343-0': 'XR Wrist - right GE 3 Views',
  '38115-2': 'XR Wrist - right Scaphoid Views',
  '26171-9': 'XR Wrist - right Views',
  '37926-3': 'XR Wrist 3 Views',
  '44190-7': 'XR Wrist GE 3 Views',
  '24619-9': 'XR Wrist Views',
};
